import type { MessageKey } from './fr';

/**
 * Arabic message catalogue.
 *
 * `Record<MessageKey, string>` (rather than `as const`) is deliberate here:
 * it makes an omitted key a compile error against the French source of
 * truth, which matters more for this file than preserving literal string
 * types.
 */
export const ar: Record<MessageKey, string> = {
  'common.loading': 'جارٍ التحميل…',
  'common.close': 'إغلاق',
  'common.dismiss': 'تجاهل',
  'common.retry': 'إعادة المحاولة',
  'common.required': 'إلزامي',
  'common.optional': 'اختياري',
  'common.tempPassword.title': 'كلمة مرور مؤقتة',
  'common.tempPassword.once':
    'تُعرض كلمة المرور هذه مرة واحدة فقط. دوّنها الآن وسلّمها عبر قناة آمنة.',
  'common.tempPassword.account': 'الحساب',
  'common.tempPassword.label': 'كلمة المرور المؤقتة',
  'common.tempPassword.show': 'إظهار',
  'common.tempPassword.hide': 'إخفاء',
  'common.tempPassword.copy': 'نسخ',
  'common.tempPassword.copied': 'تم نسخ كلمة المرور',
  'common.tempPassword.copyFailed': 'تعذّر النسخ: دوّن كلمة المرور يدويًا.',
  'common.tempPassword.expires': 'صالحة حتى {{date}}. بعد ذلك يجب إنشاء كلمة مرور جديدة.',
  'common.tempPassword.mustChange':
    'سيتعيّن على الشخص اختيار كلمة مروره الخاصة عند أول تسجيل دخول.',
  'common.tempPassword.clipboard': 'قد تحتفظ الحافظة بنسخة من كلمة المرور: امسحها بعد الاستعمال.',
  'common.tempPassword.done': 'دوّنت كلمة المرور',
  'common.tempPassword.gone': 'لم تعد كلمة المرور معروضة. إن لم تُدوَّن، أنشئ كلمة مرور جديدة.',
  'common.changePassword.current': 'كلمة المرور الحالية',
  'common.changePassword.new': 'كلمة المرور الجديدة',
  'common.changePassword.confirm': 'تأكيد كلمة المرور الجديدة',
  'common.changePassword.rules': '12 حرفًا على الأقل، ومختلفة عن كلمة المرور الحالية.',
  'common.changePassword.submit': 'حفظ كلمة المرور',
  'common.changePassword.currentRequired': 'أدخل كلمة المرور الحالية.',
  'common.changePassword.tooShort': 'يجب أن تتكوّن كلمة المرور الجديدة من 12 حرفًا على الأقل.',
  'common.changePassword.mismatch': 'كلمتا المرور غير متطابقتين.',
  'common.changePassword.same': 'اختر كلمة مرور مختلفة عن الحالية.',
  'common.selectPlaceholder': 'اختر…',

  'validation.required': 'هذا الحقل إلزامي.',
  'validation.phoneInvalid': 'رقم هاتف تونسي غير صالح.',
  'validation.governorateRequired': 'يرجى اختيار الولاية.',
  'validation.delegationRequired': 'يرجى اختيار المعتمدية.',

  'phone.countryCode': '+216',
  'phone.placeholder': '22 345 678',

  'governorate.label': 'الولاية',
  'delegation.label': 'المعتمدية',

  'pagination.label': 'ترقيم الصفحات',
  'pagination.previous': 'الصفحة السابقة',
  'pagination.next': 'الصفحة التالية',
  'pagination.page': 'صفحة',

  'stepper.label': 'التقدّم',
  'stepper.completed': 'خطوة مكتملة',
  'stepper.current': 'الخطوة الحالية',
  'stepper.upcoming': 'خطوة قادمة',

  // ---- storefront: navigation ----
  'storefront.nav.shop': 'المتجر',
  'storefront.nav.categories': 'الفئات',
  'storefront.nav.products': 'المنتجات',
  'storefront.nav.orderTracking': 'تتبع الطلب',
  'storefront.nav.cart': 'السلة',
  'storefront.nav.cartWithCount': 'السلة — {{count}} منتج',
  'storefront.nav.primary': 'التنقل الرئيسي',
  'storefront.nav.menuOpen': 'فتح القائمة',
  'storefront.nav.menuClose': 'إغلاق القائمة',
  'storefront.nav.breadcrumb': 'مسار التنقل',

  // ---- storefront: footer ----
  'storefront.footer.contact': 'معلومات الاتصال',
  'storefront.footer.hours': 'أوقات العمل',
  'storefront.footer.policies': 'السياسات',
  'storefront.footer.shipping': 'التوصيل',
  'storefront.footer.returns': 'الإرجاع',
  'storefront.footer.terms': 'الشروط العامة',
  'storefront.footer.privacy': 'الخصوصية',
  'storefront.footer.usefulLinks': 'روابط مفيدة',
  'storefront.footer.buyWithConfidence': 'تسوّق بثقة',
  'storefront.footer.cod': 'الدفع عند الاستلام',
  'storefront.footer.deliveryNational': 'توصيل إلى جميع الولايات الـ24',
  'storefront.footer.deliveryFee': 'رسوم التوصيل: {{fee}}',
  'storefront.footer.noDeliveryFee': 'بدون رسوم توصيل',
  'storefront.footer.whatsappCta': 'تواصل عبر واتساب',
  'storefront.footer.call': 'اتصل بنا',
  'storefront.footer.poweredBy': 'بدعم من ABOUEB',
  'storefront.footer.rights': '© {{year}} {{name}}',
  'storefront.footer.day.monday': 'الاثنين',
  'storefront.footer.day.tuesday': 'الثلاثاء',
  'storefront.footer.day.wednesday': 'الأربعاء',
  'storefront.footer.day.thursday': 'الخميس',
  'storefront.footer.day.friday': 'الجمعة',
  'storefront.footer.day.saturday': 'السبت',
  'storefront.footer.day.sunday': 'الأحد',

  // ---- storefront: home ----
  'storefront.home.ctaProducts': 'اكتشف المنتجات',
  'storefront.home.ctaCategories': 'تصفّح الفئات',
  'storefront.collections.title': 'مجموعاتنا',
  'storefront.collections.count': '{{count}} منتج',
  'storefront.collections.all': 'كل المجموعات',
  'storefront.story.eyebrow': 'من نحن',
  'storefront.story.defaultTitle': 'قصتنا',
  'storefront.story.cta': 'اكتشف منتجاتنا',
  'storefront.featured.eyebrow': 'اختيارات المتجر',
  'storefront.featured.title': 'مفضّلاتنا',
  'storefront.featured.subtitle': 'المنتجات التي يوصي بها {{shopName}}.',
  'storefront.featured.badge': 'اختيارنا',
  'storefront.featured.cta': 'اكتشف',
  'storefront.newArrivals.eyebrow': 'وصل حديثًا',
  'storefront.newArrivals.title': 'جديد المتجر',
  'storefront.newArrivals.badge': 'جديد',
  'storefront.newArrivals.prev': 'السابق',
  'storefront.newArrivals.next': 'التالي',
  'storefront.home.lowStockTitle': 'كمية محدودة',
  'storefront.home.lowStockBadge': 'آخر القطع',
  'storefront.home.productsTitle': 'منتجاتنا',
  'storefront.home.viewAll': 'عرض الكل',
  'storefront.home.emptyProducts': 'لم ينشر هذا المتجر أي منتج بعد.',

  // ---- storefront: reassurance bar ----
  'storefront.reassurance.codTitle': 'الدفع عند الاستلام',
  'storefront.reassurance.codDescription': 'ادفع نقداً عند استلام طردك، دون أي دفع إلكتروني.',
  'storefront.reassurance.deliveryTitle': 'توصيل وطني',
  'storefront.reassurance.returnsTitle': 'الإرجاع',
  'storefront.reassurance.returnsDescription':
    'نشر المتجر سياسة الإرجاع الخاصة به: اطّلع عليها قبل الطلب.',
  'storefront.trust.deliveryWithFee': 'إلى جميع الولايات الـ24. رسوم التوصيل: {{fee}}.',
  'storefront.trust.deliveryNoFee': 'إلى جميع الولايات الـ24، بدون رسوم توصيل.',
  'storefront.trust.deliveryStripWithFee': 'إلى جميع الولايات الـ24 · الرسوم: {{fee}}',
  'storefront.trust.deliveryStripNoFee': 'إلى جميع الولايات الـ24 · بدون رسوم',
  'storefront.trust.returnsSummary': 'سياسة الإرجاع الخاصة بالمتجر',
  'storefront.trust.contactTitle': 'لديك سؤال؟ اكتب إلى المتجر أو اتصل به.',
  'storefront.trust.email': 'إرسال بريد إلكتروني',
  'storefront.common.noFee': 'بدون رسوم',
  'storefront.reassurance.supportTitle': 'خدمة العملاء',

  // ---- storefront: categories page ----
  'storefront.categories.title': 'الفئات',
  'storefront.categories.empty': 'لم ينشر هذا المتجر أي فئة بعد.',
  'storefront.categories.count': '{{count}} مجموعة',
  'storefront.categories.discover': 'اكتشف',
  'storefront.categories.moreLabel': '{{count}} فئات فرعية أخرى في {{name}}',

  // ---- storefront: products (catalogue) page ----
  'storefront.products.title': 'المنتجات',
  'storefront.products.viewAll': 'عرض جميع المنتجات',
  'storefront.products.emptyCategory': 'لا يوجد أي منتج منشور في هذه الفئة حالياً.',
  'storefront.products.emptyAll': 'لم ينشر هذا المتجر أي منتج بعد.',
  'storefront.catalog.allProducts': 'كل المنتجات',
  'storefront.catalog.chipAll': 'الكل',
  'storefront.catalog.chipsLabel': 'الفئات',
  'storefront.catalog.count': '{{count}} منتج',
  'storefront.catalog.filter': 'تصفية',
  'storefront.catalog.filterWithCount': 'تصفية · {{count}}',
  'storefront.catalog.sortLabel': 'ترتيب حسب',
  'storefront.catalog.sort.featured': 'المختارة',
  'storefront.catalog.sort.newest': 'الأحدث',
  'storefront.catalog.sort.priceAsc': 'السعر: من الأقل',
  'storefront.catalog.sort.priceDesc': 'السعر: من الأعلى',
  'storefront.catalog.sort.nameAsc': 'الاسم أ–ي',
  'storefront.catalog.filters.title': 'عوامل التصفية',
  'storefront.catalog.filters.close': 'إغلاق',
  'storefront.catalog.filters.price': 'السعر',
  'storefront.catalog.filters.priceMin': 'الأدنى',
  'storefront.catalog.filters.priceMax': 'الأقصى',
  'storefront.catalog.filters.currency': 'د.ت',
  'storefront.catalog.filters.availability': 'التوفر',
  'storefront.catalog.filters.inStock': 'المتوفر فقط',
  'storefront.catalog.filters.promotions': 'العروض',
  'storefront.catalog.filters.onSale': 'المنتجات المخفّضة',
  'storefront.catalog.filters.brand': 'العلامة',
  'storefront.catalog.filters.clear': 'مسح',
  'storefront.catalog.filters.apply': 'عرض {{count}} نتيجة',
  'storefront.catalog.active.groupLabel': 'عوامل التصفية المطبّقة',
  'storefront.catalog.active.priceRange': '{{min}} – {{max}} د.ت',
  'storefront.catalog.active.priceFrom': 'ابتداءً من {{min}} د.ت',
  'storefront.catalog.active.priceUpTo': 'حتى {{max}} د.ت',
  'storefront.catalog.active.inStock': 'متوفر',
  'storefront.catalog.active.onSale': 'مخفّض',
  'storefront.catalog.active.clearAll': 'مسح الكل',
  'storefront.catalog.active.remove': 'إزالة عامل التصفية: {{label}}',
  'storefront.catalog.showMore': 'عرض {{count}} أخرى',
  'storefront.catalog.progress': 'شاهدت {{shown}} من أصل {{total}} منتجًا',
  'storefront.catalog.emptyFiltered': 'لا يوجد منتج يطابق هذه التصفية.',
  'storefront.catalog.clearFilters': 'مسح التصفية',
  'storefront.card.add': 'أضف',
  'storefront.card.chooseOptions': 'اختر الخيارات',
  'storefront.card.unavailable': 'غير متوفر',
  'storefront.card.soldOut': 'نفدت الكمية',
  'storefront.card.lowStockLine': 'بقي {{count}} فقط',
  'storefront.wishlist.add': 'أضف إلى المفضلة',
  'storefront.wishlist.remove': 'إزالة من المفضلة',
  'storefront.wishlist.navLabel': 'مفضلتي',
  'storefront.wishlist.navLabelWithCount': 'مفضلتي — {{count}}',
  'storefront.wishlist.title': 'مفضلتي',
  'storefront.wishlist.deviceNote': 'المفضلة محفوظة على هذا الجهاز',
  'storefront.wishlist.deviceNoteSub': 'ليست مرتبطة بحساب.',
  'storefront.wishlist.empty': 'ليست لديك منتجات مفضلة بعد.',
  'storefront.wishlist.emptyHint': 'اضغط على القلب في أي منتج لتجده هنا.',
  'storefront.wishlist.emptyCta': 'تصفّح الكتالوج',
  'storefront.wishlist.loadError': 'تعذّر تحميل المفضلة حاليًا.',

  // ---- storefront: product detail ----
  'storefront.product.similar': 'قد يعجبك أيضاً',
  'storefront.product.viewPhoto': 'عرض الصورة {{n}}',
  'storefront.product.addToCart': 'أضف إلى السلة',
  'storefront.product.addShort': 'أضف',
  'storefront.product.adding': 'جارٍ الإضافة…',
  'storefront.product.addedToCart': 'أُضيف إلى السلة.',
  'storefront.product.quickAddLabel': 'إضافة سريعة إلى السلة',
  'storefront.product.quickAddedLabel': 'أُضيف إلى السلة',
  'storefront.product.notFoundTitle': 'المنتج غير موجود',
  'storefront.product.notFoundDescription': 'هذا المنتج غير موجود أو لم يعد متوفراً.',
  'storefront.product.selectOptionsPrompt': 'اختر خيارًا',
  'storefront.product.combinationUnavailable': 'هذا المزيج غير متوفر.',

  // ---- storefront: stock states ----
  'storefront.stock.inStock': 'متوفر',
  'storefront.stock.inStockCount': 'متوفر · {{count}}',
  'storefront.stock.lowStock': 'كمية محدودة',
  'storefront.stock.outOfStock': 'نفدت الكمية',
  'storefront.stock.unavailable': 'غير متوفر',
  'storefront.stock.badgeOutOfStock': 'نفدت الكمية',
  'storefront.stock.badgeLowStock': 'بقي {{count}} فقط',

  // ---- storefront: shared across cart / checkout / order lookup ----
  'storefront.common.subtotal': 'المجموع الفرعي',
  'storefront.common.discount': 'الخصم',
  'storefront.common.deliveryFee': 'التوصيل',
  'storefront.common.total': 'المجموع',
  'storefront.common.codReassurance': 'الدفع عند الاستلام — لا تدفع إلا عند استلام طردك.',
  'storefront.common.backToShop': 'العودة إلى المتجر',
  'storefront.common.emptyCart': 'سلتك فارغة.',

  // ---- storefront: cart page ----
  'storefront.cart.title': 'السلة',
  'storefront.cart.remove': 'إزالة',
  'storefront.cart.removedMessage': 'تمت إزالة {{name}} من السلة.',
  'storefront.cart.couponPlaceholder': 'رمز الخصم',
  'storefront.cart.couponLabel': 'رمز الخصم',
  'storefront.cart.couponApply': 'تطبيق',
  'storefront.cart.couponApplied': 'تم تطبيق الرمز «{{code}}».',
  'storefront.cart.couponInvalid': 'الرمز «{{code}}» لم يعد صالحاً وتمت إزالته.',
  'storefront.cart.checkoutCta': 'إتمام الطلب',
  'storefront.cart.continueShopping': '← متابعة التسوق',

  // ---- storefront: checkout page ----
  'storefront.checkout.title': 'الطلب',
  'storefront.checkout.stepContact': 'المعلومات الشخصية',
  'storefront.checkout.stepDelivery': 'التوصيل',
  'storefront.checkout.stepSummary': 'الملخص',
  'storefront.checkout.submitPending': 'جارٍ التأكيد…',
  'storefront.checkout.submit': 'تأكيد الطلب',
  'storefront.checkout.sectionContact': 'المعلومات الشخصية',
  'storefront.checkout.fieldName': 'الاسم الكامل',
  'storefront.checkout.fieldPhone': 'الهاتف',
  'storefront.checkout.fieldEmail': 'البريد الإلكتروني (اختياري)',
  'storefront.checkout.sectionDelivery': 'التوصيل',
  'storefront.checkout.fieldGovernorate': 'الولاية',
  'storefront.checkout.fieldGovernoratePlaceholder': 'اختر…',
  'storefront.checkout.fieldDelegation': 'المعتمدية',
  'storefront.checkout.fieldDelegationFirst': 'اختر الولاية أولاً',
  'storefront.checkout.errors.delegationMismatch': 'هذه المعتمدية لا تنتمي إلى الولاية المختارة.',
  'storefront.checkout.fieldAddress': 'العنوان',
  'storefront.checkout.fieldAddressExtra': 'تفاصيل إضافية للعنوان (اختياري)',
  'storefront.checkout.fieldNotes': 'ملاحظات (اختياري)',
  'storefront.checkout.summaryTitle': 'الملخص',
  'storefront.checkout.editCart': 'تعديل السلة',
  'storefront.checkout.codBadge': 'الأكثر طلباً',
  'storefront.checkout.codLabel': 'الدفع عند الاستلام',
  'storefront.checkout.noShippingFee': 'لا يفرض هذا المتجر رسوم توصيل.',
  'storefront.checkout.successTitle': 'تم تأكيد الطلب!',
  'storefront.checkout.successPrefix': 'تم تسجيل طلبك',
  'storefront.checkout.successSuffix': 'بنجاح.',
  'storefront.checkout.successTotal': 'المبلغ المستحق',
  'storefront.checkout.successCod': 'الدفع عند الاستلام — لا تدفع إلا عند استلام طردك.',

  // ---- storefront: order lookup ----
  'storefront.checkout.errors.nameRequired': 'الاسم مطلوب.',
  'storefront.checkout.errors.phoneInvalid':
    'رقم هاتف غير صالح: 8 أرقام، مع أو بدون +216، ولا يبدأ بـ 0 أو 1.',
  'storefront.checkout.errors.governorateRequired': 'الولاية مطلوبة.',
  'storefront.checkout.errors.delegationRequired': 'المعتمدية مطلوبة.',
  'storefront.checkout.errors.addressRequired': 'العنوان مطلوب.',
  'storefront.orderLookup.errors.orderNumberInvalid': 'رقم طلب غير صالح: أرقام فقط.',
  'storefront.orderLookup.title': 'تتبع الطلب',
  'storefront.orderLookup.subtitle':
    'ابحث عن طلبك باستخدام رقم الطلب ورقم الهاتف المستخدم عند الشراء.',
  'storefront.orderLookup.orderNumberLabel': 'رقم الطلب',
  'storefront.orderLookup.phoneLabel': 'الهاتف المستخدم عند الطلب',
  'storefront.orderLookup.submit': 'بحث',
  'storefront.orderLookup.submitPending': 'جارٍ البحث…',
  'storefront.orderLookup.resultTitle': 'الطلب',
  'storefront.orderLookup.deliveryTitle': 'التوصيل',
  'storefront.orderLookup.trackingTitle': 'التتبع',
  'storefront.orderLookup.parcel.title': 'طردك',
  'storefront.orderLookup.parcel.tracking': 'رقم التتبع',
  'storefront.orderLookup.parcel.updated': 'آخر تحديث في',
  'storefront.orderLookup.parcel.status.registered': 'الطرد مسجّل، في انتظار الاستلام',
  'storefront.orderLookup.parcel.status.pickup_scheduled': 'الاستلام مُبرمج',
  'storefront.orderLookup.parcel.status.in_transit': 'في الطريق',
  'storefront.orderLookup.parcel.status.out_for_delivery': 'قيد التسليم',
  'storefront.orderLookup.parcel.status.delivered': 'تم التسليم',
  'storefront.orderLookup.parcel.status.return_in_progress': 'تعذّر التسليم — الإرجاع جارٍ',
  'storefront.orderLookup.parcel.status.returned': 'أُرجع الطرد إلى المتجر',
  'storefront.orderLookup.parcel.status.checking': 'نتحقق من طردك — سيتصل بك المتجر',
  'storefront.orderLookup.parcel.status.cancelled': 'أُلغي الشحن',
  'storefront.orderLookup.parcel.status.other': 'قيد المتابعة',

  // ---- storefront: order status vocabulary ----
  'storefront.orderStatus.ORDER_CREATED': 'تم الإنشاء',
  'storefront.orderStatus.PAYMENT_PENDING': 'الدفع قيد الانتظار',
  'storefront.orderStatus.PAYMENT_CONFIRMED': 'الدفع عند الاستلام',
  'storefront.orderStatus.PAYMENT_FAILED': 'فشل الدفع',
  'storefront.orderStatus.PROCESSING': 'قيد التحضير',
  'storefront.orderStatus.PACKED': 'قيد التوصيل',
  // The merchant sees the real step (handed to the carrier, not yet collected); the customer, a simpler « en livraison ».
  'merchant.orderStatus.PACKED': 'جاهز للشحن',
  'storefront.orderStatus.CANCELLED': 'ملغاة',
  'storefront.orderStatus.SHIPPED': 'قيد التوصيل',
  'storefront.orderStatus.DELIVERY_FAILED': 'فشل التوصيل',
  'storefront.orderStatus.DELIVERED': 'تم التسليم',
  'storefront.orderStatus.RETURN_REQUESTED': 'طلب إرجاع',
  'storefront.orderStatus.RETURNED': 'تم الإرجاع',
  'storefront.orderStatus.REFUNDED': 'تم استرداد المبلغ',
  'storefront.orderStatus.PARTIALLY_REFUNDED': 'استرداد جزئي للمبلغ',

  // ---- storefront: not-found boundaries ----
  'storefront.notFound.shopTitle': 'المتجر غير موجود',
  'storefront.notFound.shopDescription': 'هذا المتجر غير موجود أو غير متاح حالياً.',

  // ---- storefront: platform landing ----
  'storefront.landing.title': 'ABOUEB',
  'storefront.landing.description':
    'لكل متجر على منصة ABOUEB عنوانه العام الخاص به. توجّه مباشرة إلى رابط المتجر الذي تبحث عنه للاطلاع على كتالوجه.',

  // ---- merchant: shared layout (sidebar, topbar, command palette, shop switcher) ----
  'merchant.sidebar.brand': 'ABOUEB',
  'merchant.sidebar.subtitle': 'مساحة التاجر',
  'merchant.sidebar.navLabel': 'التنقل الرئيسي',
  'merchant.topbar.menuClose': 'إغلاق القائمة',
  'merchant.sidebar.collapse': 'تصغير القائمة',
  'merchant.sidebar.expand': 'توسيع القائمة',

  'merchant.nav.group.principal': 'الرئيسية',
  'merchant.nav.group.operations': 'العمليات',
  'merchant.nav.group.configuration': 'الإعدادات',
  'merchant.nav.section.general': 'عام',
  'merchant.nav.section.sales': 'المبيعات',
  'merchant.nav.section.growth': 'النمو',
  'merchant.nav.section.manage': 'الإدارة',
  'merchant.nav.dashboard': 'لوحة التحكم',
  'merchant.nav.orders': 'الطلبات',
  'merchant.nav.products': 'المنتجات والمخزون',
  'merchant.nav.inventory': 'الجرد',
  'merchant.nav.customers': 'العملاء',
  'merchant.nav.marketing': 'التسويق',
  'merchant.nav.analytics': 'التحليلات',
  'merchant.nav.delivery': 'التوصيل',
  'merchant.nav.deliveryAccount': 'حساب جيبكس',
  'merchant.nav.payments': 'المدفوعات والدفع عند الاستلام',
  'merchant.nav.invoices': 'الفواتير',
  'merchant.nav.store': 'المتجر',
  'merchant.nav.categories': 'الفئات',
  'merchant.nav.promotions': 'العروض الترويجية',
  'merchant.nav.team': 'الفريق والصلاحيات',
  'merchant.nav.settings': 'الإعدادات العامة',
  'merchant.nav.menu.products': 'المنتجات',
  'merchant.nav.menu.payments': 'المدفوعات',

  'merchant.topbar.searchPlaceholder': 'بحث، الانتقال إلى…',
  'merchant.topbar.themeToggleAria': 'تغيير المظهر',
  'merchant.topbar.lightMode': 'الوضع الفاتح',
  'merchant.topbar.darkMode': 'الوضع الداكن',
  'merchant.topbar.logout': 'تسجيل الخروج',
  'merchant.topbar.menu': 'فتح القائمة',
  'merchant.topbar.more': 'خيارات أخرى',

  'merchant.shopSwitcher.none': 'لا يوجد متجر',
  'merchant.shopSwitcher.unnamed': 'متجر بدون اسم',
  'merchant.shopSwitcher.activeAria': 'المتجر النشط',
  'merchant.shopSwitcher.suspended': 'موقوف',

  'merchant.commandPalette.label': 'لوحة الأوامر',
  'merchant.commandPalette.placeholder': 'ابحث عن شاشة أو إجراء…',
  'merchant.commandPalette.empty': 'لا توجد نتائج.',
  'merchant.commandPalette.navigationGroup': 'التنقل',
  'merchant.commandPalette.preferencesGroup': 'التفضيلات',
  'merchant.commandPalette.toggleThemeToLight': 'التبديل إلى الوضع الفاتح',
  'merchant.commandPalette.toggleThemeToDark': 'التبديل إلى الوضع الداكن',

  'merchant.common.loadingAria': 'جارٍ التحميل',
  'merchant.common.noOrdersYet': 'لا توجد طلبات حتى الآن',
  'merchant.common.unexpectedError': 'حدث خطأ غير متوقع.',
  'merchant.common.cancel': 'إلغاء',
  'merchant.common.confirm': 'تأكيد',
  'merchant.common.save': 'حفظ',
  'merchant.common.edit': 'تعديل',
  'merchant.common.delete': 'حذف',
  'merchant.common.accessDenied': 'الوصول مرفوض',

  // ---- merchant: dashboard ----
  'merchant.dashboard.title': 'لوحة التحكم',
  'merchant.dashboard.hello': 'مرحبًا {{name}}،',
  'merchant.dashboard.heroLead': 'إليك متجرك اليوم.',
  'merchant.dashboard.addProduct': 'إضافة منتج',
  'merchant.dashboard.rangeAria': 'الفترة',
  'merchant.dashboard.rangeDaysZero': '{{count}} يوم',
  'merchant.dashboard.rangeDaysOne': 'يوم واحد',
  'merchant.dashboard.rangeDaysTwo': 'يومان',
  'merchant.dashboard.rangeDaysFew': '{{count}} أيام',
  'merchant.dashboard.rangeDaysMany': '{{count}} يومًا',
  'merchant.dashboard.rangeDaysOther': '{{count}} يوم',
  'merchant.dashboard.kpiOrdersRange': 'الطلبات',
  'merchant.dashboard.kpiOrdersRangeHint': 'خلال {{days}}',
  'merchant.dashboard.stockLow': 'مخزون منخفض',
  'merchant.dashboard.stockOut': 'نفاد المخزون',
  'merchant.dashboard.salesEyebrow': 'الأداء',
  'merchant.dashboard.salesLegend': 'المبيعات',
  'merchant.dashboard.salesTrend.subtitleRange': 'خلال {{days}}، دون الطلبات الملغاة',
  'merchant.dashboard.salesTrend.totalRange': 'الإجمالي خلال {{days}}',
  'merchant.dashboard.ordersEyebrow': 'تدفق الطلبات',
  'merchant.dashboard.ordersTitle': 'حالة الطلبات',
  'merchant.dashboard.viewAll': 'عرض الكل',
  'merchant.dashboard.ordersFilterAll': 'الكل',
  'merchant.dashboard.ordersFilterOpen': 'قيد المعالجة',
  'merchant.dashboard.ordersFilterFinished': 'منتهية',
  'merchant.dashboard.ordersTotal': 'إجمالي الطلبات',
  'merchant.dashboard.attentionEyebrow': 'الأولويات',
  'merchant.dashboard.attentionTitle': 'يتطلب انتباهك',
  'merchant.dashboard.attentionCount': '{{count}} للمعالجة',
  'merchant.dashboard.alerts.actionProcess': 'معالجة',
  'merchant.dashboard.alerts.actionCheck': 'تحقّق',
  'merchant.dashboard.alerts.actionView': 'عرض',
  'merchant.dashboard.quickEyebrow': 'اختصارات',
  'merchant.dashboard.quick.title': 'ماذا تريد أن تفعل؟',
  'merchant.dashboard.quick.newProductDescription': 'أضف منتجًا إلى كتالوجك',
  'merchant.dashboard.quick.viewOrdersDescription': 'عالج الطلبات المعلّقة',
  'merchant.dashboard.quick.inventory': 'إدارة المخزون',
  'merchant.dashboard.quick.inventoryDescription': 'تابع مستويات المخزون',
  'merchant.dashboard.quick.newCouponDescription': 'أطلق عرضًا ترويجيًا',
  'merchant.dashboard.quick.customers': 'إدارة العملاء',
  'merchant.dashboard.quick.customersDescription': 'اطّلع على قاعدة عملائك',
  'merchant.dashboard.quick.store': 'إعداد المتجر',
  'merchant.dashboard.quick.storeDescription': 'خصّص متجرك',
  'merchant.dashboard.shareEyebrow': 'متجرك',
  'merchant.dashboard.shareTitle': 'شارك {{name}}',
  'merchant.dashboard.shareText': 'عرّف مجتمعك بمتجرك.',
  'merchant.dashboard.shareCopy': 'نسخ',
  'merchant.dashboard.shareCopied': 'تم النسخ',
  'merchant.dashboard.topEyebrow': 'الأكثر مبيعًا',
  'merchant.dashboard.subtitle': 'ما بعته، طلباتك، وما يحتاج إلى اهتمامك.',
  'merchant.dashboard.loadError': 'تعذر تحميل لوحة التحكم',
  'merchant.dashboard.accessDeniedDescription': 'حسابك لا يملك صلاحية الاطلاع على التحليلات.',
  'merchant.dashboard.kpiTodayRevenue': 'إيرادات اليوم',
  'merchant.dashboard.kpiTodayHint': '{{count}} طلب(ات) اليوم',
  'merchant.dashboard.kpiMonthRevenue': 'إيرادات الشهر',
  'merchant.dashboard.kpiMonthHint': '{{count}} طلب(ات) هذا الشهر',
  'merchant.dashboard.kpiOrderCount': 'عدد الطلبات',
  'merchant.dashboard.kpiOrderCountHint': 'منذ افتتاح المتجر',
  'merchant.dashboard.kpiActiveProducts': 'المنتجات النشطة',
  'merchant.dashboard.kpiTodayHintZero': 'لا طلبات اليوم',
  'merchant.dashboard.kpiTodayHintOne': 'طلب واحد اليوم',
  'merchant.dashboard.kpiTodayHintTwo': 'طلبان اليوم',
  'merchant.dashboard.kpiTodayHintFew': '{{count}} طلبات اليوم',
  'merchant.dashboard.kpiTodayHintMany': '{{count}} طلبًا اليوم',
  'merchant.dashboard.kpiTodayHintOther': '{{count}} طلب اليوم',
  'merchant.dashboard.kpiMonthHintZero': 'لا طلبات هذا الشهر',
  'merchant.dashboard.kpiMonthHintOne': 'طلب واحد هذا الشهر',
  'merchant.dashboard.kpiMonthHintTwo': 'طلبان هذا الشهر',
  'merchant.dashboard.kpiMonthHintFew': '{{count}} طلبات هذا الشهر',
  'merchant.dashboard.kpiMonthHintMany': '{{count}} طلبًا هذا الشهر',
  'merchant.dashboard.kpiMonthHintOther': '{{count}} طلب هذا الشهر',
  'merchant.dashboard.kpiMonthRevenueShort': 'رقم الشهر',
  'merchant.dashboard.kpiOrderCountShort': 'الطلبات',
  'merchant.dashboard.kpiActiveProductsShort': 'المنتجات',
  'merchant.dashboard.trendStable': 'مستقر مقارنة بالأمس',
  'merchant.dashboard.trendChange': '{{value}}% مقارنة بالأمس',
  'merchant.dashboard.trendNewActivity': 'نشاط جديد مقارنة بالأمس',
  'merchant.dashboard.statusByStatusTitle': 'الطلبات حسب الحالة',
  'merchant.dashboard.statusByStatusSubtitle': 'جميع الطلبات، منذ الافتتاح',
  'merchant.dashboard.statusBreakdown.emptyDescription':
    'سيظهر التوزيع حسب الحالة هنا فور وصول أول طلب.',
  'merchant.dashboard.kpiSoonBadge': 'قريباً',
  'merchant.dashboard.kpiSoonDefaultReason': 'الوحدة لم تُبنَ بعد.',

  'merchant.dashboard.alerts.title': 'التنبيهات',
  'merchant.dye.title': 'طلباتك، مرحلة بمرحلة',
  'merchant.dye.subtitle': 'جميع الطلبات منذ الافتتاح، من الاستلام إلى التوصيل',
  'merchant.dye.stage.received': 'مستلمة',
  'merchant.dye.stage.toPrepare': 'للتحضير',
  'merchant.dye.stage.ready': 'جاهزة',
  'merchant.dye.stage.shipping': 'قيد التوصيل',
  'merchant.dye.stage.delivered': 'تم توصيلها',
  'merchant.dye.outOfFlow': 'خارج المسار',
  'merchant.dye.processZero': 'لا طلبات للمعالجة',
  'merchant.dye.processOne': 'معالجة طلب واحد',
  'merchant.dye.processTwo': 'معالجة طلبين',
  'merchant.dye.processFew': 'معالجة {{count}} طلبات',
  'merchant.dye.processMany': 'معالجة {{count}} طلبًا',
  'merchant.dye.processOther': 'معالجة {{count}} طلب',
  'merchant.dye.allClear': 'لا شيء للتحضير حاليًا',
  'merchant.dye.viewStage': 'عرض الطلبات «{{stage}}»',
  'merchant.dye.empty': 'سيظهر طلبك الأول هنا، مرحلة بمرحلة.',
  'merchant.dashboard.updatedAt': 'آخر تحديث على الساعة {{time}}',
  'merchant.dashboard.figuresLabel': 'الأرقام الرئيسية',
  'merchant.dashboard.alerts.checking': 'جارٍ التحقق…',
  'merchant.dashboard.alerts.allClear':
    'لا شيء يستدعي الانتباه — المخزون والطلبات والقسائم تحت السيطرة.',
  'merchant.dashboard.alerts.lowStockOne': 'منتج واحد يستحق المراقبة من حيث المخزون',
  'merchant.dashboard.alerts.lowStockMany': '{{count}} منتجات تستحق المراقبة من حيث المخزون',
  'merchant.dashboard.alerts.lowStockOutOfStock': 'من بينها {{count}} نفدت من المخزون.',
  'merchant.dashboard.alerts.lowStockBelowThreshold': 'المخزون المتاح أقل من حد التنبيه.',
  'merchant.dashboard.alerts.actionableOrdersOne': 'طلب واحد بحاجة للمعالجة',
  'merchant.dashboard.alerts.actionableOrdersMany': '{{count}} طلبات بحاجة للمعالجة',
  'merchant.dashboard.alerts.actionableOrdersDescription': 'في انتظار التحضير أو التغليف.',
  'merchant.dashboard.alerts.expiringCouponsOne': 'قسيمة واحدة تنتهي صلاحيتها قريباً',
  'merchant.dashboard.alerts.expiringCouponsMany': '{{count}} قسائم تنتهي صلاحيتها قريباً',
  'merchant.dashboard.alerts.expiringCouponsDescription': 'خلال الأيام {{days}} القادمة.',

  'merchant.dashboard.quickActions.title': 'إجراءات سريعة',
  'merchant.dashboard.quickActions.newProduct': 'منتج جديد',
  'merchant.dashboard.quickActions.newCoupon': 'قسيمة جديدة',
  'merchant.dashboard.quickActions.viewOrders': 'عرض الطلبات',

  'merchant.dashboard.salesTrend.loadError': 'تعذر تحميل تطور المبيعات',
  'merchant.dashboard.salesTrend.dayOrdersZero': 'لا طلبات',
  'merchant.dashboard.salesTrend.dayOrdersOne': 'طلب واحد',
  'merchant.dashboard.salesTrend.dayOrdersTwo': 'طلبان',
  'merchant.dashboard.salesTrend.dayOrdersFew': '{{count}} طلبات',
  'merchant.dashboard.salesTrend.dayOrdersMany': '{{count}} طلبًا',
  'merchant.dashboard.salesTrend.dayOrdersOther': '{{count}} طلب',
  'merchant.dashboard.salesTrend.title': 'تطور المبيعات',
  'merchant.dashboard.salesTrend.subtitle': 'آخر 14 يوماً — الطلبات المؤكدة، باستثناء الملغاة',
  'merchant.dashboard.salesTrend.legendCurrency': 'الإيرادات (دينار)',
  'merchant.dashboard.salesTrend.emptyTitle': 'لا مبيعات خلال هذه الفترة',
  'merchant.dashboard.salesTrend.emptyDescription':
    'سيمتلئ الرسم البياني فور استلام أول طلب غير ملغى.',
  'merchant.dashboard.salesTrend.chartAria': 'رسم بياني لتطور المبيعات خلال آخر 14 يوماً',
  'merchant.dashboard.salesTrend.totalLabel': 'الإجمالي خلال الفترة:',

  'merchant.dashboard.topProducts.title': 'أفضل المنتجات',
  'merchant.dashboard.topProducts.emptyTitle': 'لا يوجد منتج مباع حتى الآن',
  'merchant.dashboard.topProducts.emptyDescription':
    'سيظهر الترتيب هنا فور استلام أول طلب غير ملغى.',
  'merchant.dashboard.topProducts.soldCount': 'تم بيع {{count}}',

  // ---- merchant: orders ----
  'merchant.orders.title': 'الطلبات',
  'merchant.orders.subtitle': 'تابع الطلبات الواردة وعالجها.',
  'merchant.orders.filterAllStatuses': 'كل الحالات',
  'merchant.orders.filterAria': 'تصفية حسب الحالة',
  'merchant.orders.loadError': 'تعذر تحميل الطلبات',
  'merchant.orders.accessDeniedDescription': 'حسابك لا يملك صلاحية إدارة الطلبات.',
  'merchant.orders.paginationSummary': 'الطلبات {{from}}–{{to}} من أصل {{total}}',
  'merchant.orders.paginationPageSuffix': ' · صفحة {{page}} من {{pages}}',
  'merchant.orders.prev': 'السابق',
  'merchant.orders.next': 'التالي',
  'merchant.orders.table.colOrder': 'الطلب',
  'merchant.orders.table.colCustomer': 'العميل',
  'merchant.orders.table.colStatus': 'الحالة',
  'merchant.orders.table.colTotal': 'الإجمالي',
  'merchant.orders.table.colDate': 'التاريخ',
  'merchant.orders.itemCount': '{{count}} عنصر',
  'merchant.orders.actionProcessing': 'وضع علامة قيد التحضير',
  'merchant.orders.actionPacked': 'نقل إلى التوصيل',
  'merchant.orders.completed': 'منتهية',
  'merchant.orders.noActionNeeded': 'لا حاجة لأي إجراء',
  'merchant.orders.updateModalTitle': 'تحديث الطلب',
  'merchant.orders.statusUpdated': 'تم تحديث حالة الطلب.',
  'merchant.orders.targetProcessing': 'وضع علامة «قيد التحضير»',
  'merchant.orders.targetPacked': 'نقل إلى «قيد التوصيل»',
  'merchant.orders.targetCancel': 'إلغاء الطلب',
  'merchant.orders.reasonLabel': 'السبب',

  // ---- merchant: store ----
  'merchant.store.subtitleNew': 'أعدّ متجرك لتبدأ البيع.',
  'merchant.store.subtitleExisting': 'أدر المعلومات العامة لمتجرك.',
  'merchant.store.loadError': 'تعذر تحميل المتجر',
  'merchant.store.updated': 'تم تحديث المتجر.',
  'merchant.store.nextStep.title': 'تم حفظ المتجر',
  'merchant.store.nextStep.description': 'الخطوة التالية الموصى بها: أنشئ أول فئة لتنظيم منتجاتك.',
  'merchant.store.nextStep.cta': 'إنشاء فئة',
  'merchant.store.ownerOnlyNotice':
    'مالك المتجر وحده يمكنه تعديل هذه المعلومات. يمكنك الاطلاع على النموذج أدناه دون حفظه.',
  'merchant.store.createSubmit': 'إنشاء المتجر',
  'merchant.store.saveSubmit': 'حفظ التعديلات',

  'merchant.store.form.identity': 'الهوية',
  'merchant.store.form.essentials': 'المعلومات الأساسية',
  'merchant.store.form.general': 'معلومات عامة',
  'merchant.store.form.nameLabel': 'اسم المتجر',
  'merchant.store.form.slugLabel': 'المعرّف (Slug)',
  'merchant.store.form.slugHint': 'يُستخدم في الرابط العام — /shop/:slug.',
  'merchant.store.form.prefillNotice':
    'جهّزنا متجرك انطلاقًا من حسابك « {{name}} ». راجع الاسم والعنوان وعدّلهما إن أردت، ثم أنشئ المتجر.',
  'merchant.store.form.slugManualHint':
    'لا يحتوي هذا الاسم على أحرف لاتينية: اختر عنوانًا بأحرف لاتينية وأرقام وشرطات (مثل my-shop).',
  'merchant.store.form.slugTaken': 'هذا العنوان مستعمل من متجر آخر. اختر عنوانًا مختلفًا.',
  'merchant.store.form.descriptionLabel': 'الوصف',
  'merchant.store.form.logoUrlLabel': 'الشعار',
  'merchant.store.form.logoUrlHint':
    'يظهر في رأس متجرك وبانر الصفحة الرئيسية وتذييل الصفحة. الأفضل: صورة مربعة بعرض 400 بكسل على الأقل.',
  'merchant.images.belowRecommended':
    'تم حفظ «{{name}}»، لكنها صغيرة ({{width}} × {{height}} بكسل) وقد تبدو غير واضحة عند عرضها. المقاس الموصى به: {{recommendedWidth}} × {{recommendedHeight}} بكسل على الأقل.',
  'merchant.store.form.previewUnavailable': 'الصورة محفوظة، لكن تعذّر تحميل معاينتها.',
  'merchant.store.form.taglineLabel': 'الشعار النصي',
  'merchant.store.form.taglineHint': 'جملة قصيرة تظهر تحت اسم متجرك (160 حرفًا كحد أقصى).',
  'merchant.store.form.story': 'قصتنا',
  'merchant.store.form.storyTitleLabel': 'العنوان',
  'merchant.store.form.storyTitleHint': 'اتركه فارغًا لعرض «قصتنا» (120 حرفًا كحد أقصى).',
  'merchant.store.form.storyBodyLabel': 'قصتك',
  'merchant.store.form.storyBodyHint':
    'من أنتم، ماذا تبيعون، ولماذا وُجدتم؟ افصلوا بين الفقرات بسطر فارغ (2000 حرف كحد أقصى، نص عادي).',
  'merchant.store.form.whatsappLabel': 'واتساب',
  'merchant.store.form.whatsappHint':
    'مع رمز الدولة، مثال: +216 22 123 456. يظهر زر واتساب في متجرك.',
  'merchant.store.form.phoneLabel': 'الهاتف',
  'merchant.store.form.emailLabel': 'البريد الإلكتروني',
  'merchant.store.form.addressLabel': 'العنوان',
  'merchant.store.form.seo': 'تحسين محركات البحث (SEO)',
  'merchant.store.form.seoTitleLabel': 'عنوان SEO',
  'merchant.store.form.seoDescriptionLabel': 'وصف SEO',
  'merchant.store.form.stockDisplayPolicy': 'عرض المخزون في المتجر',
  'merchant.store.form.stockDisplayPolicy.exact': 'الكمية الدقيقة',
  'merchant.store.form.stockDisplayPolicy.exact.hint': 'عرض العدد الحقيقي للوحدات المتاحة دائمًا.',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly': '"باقي X فقط" عند انخفاض المخزون',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly.hint':
    'يظهر الرقم الدقيق فقط عندما يكون المخزون منخفضًا أو نافدًا — موصى به.',
  'merchant.store.form.stockDisplayPolicy.hidden': 'عدم عرض الكمية أبدًا',
  'merchant.store.form.stockDisplayPolicy.hidden.hint':
    'تظهر الحالة فقط (متوفر / منخفض / نافد)، وليس الرقم أبدًا.',
  'merchant.store.form.deliveryFeeLabel': 'رسوم التوصيل (د.ت)',
  'merchant.store.form.deliveryFeeHint':
    'تُضاف إلى المجموع الفرعي لكل طلب. اتركها عند 0 لعدم فرض أي رسوم توصيل.',
  'merchant.store.form.branding': 'الهوية البصرية',
  'merchant.store.form.brandingAdvanced': 'إعدادات متقدمة',
  'merchant.store.form.bannerUrlLabel': 'بانر الصفحة الرئيسية',
  'merchant.store.form.bannerUrlHint':
    'يُعرض بعرض كامل أعلى صفحتك الرئيسية. الأفضل: صورة عريضة لا تقل عن 1600 × 600 بكسل.',
  'merchant.store.form.colorPrimaryLabel': 'اللون الأساسي',
  'merchant.store.form.colorPrimaryHint': 'يهيمن على بانر الصفحة الرئيسية وعناصر العلامة التجارية.',
  'merchant.store.form.colorSecondaryLabel': 'اللون الثانوي',
  'merchant.store.form.colorSecondaryHint': 'يُستخدم في الأشرطة والأقسام الداعمة.',
  'merchant.store.form.colorAccentLabel': 'لون التمييز',
  'merchant.store.form.colorAccentHint': 'يُستخدم للزر الرئيسي، افتراضيًا.',
  'merchant.store.form.colorTextLabel': 'لون النص',
  'merchant.store.form.colorTextHint': 'اتركه فارغًا للاحتفاظ بلون النص المحسوب تلقائيًا.',
  'merchant.store.form.colorButtonLabel': 'لون الأزرار',
  'merchant.store.form.colorButtonHint': 'يعتمد على لون التمييز إذا تُرك فارغًا.',
  'merchant.store.form.colorBadgeLabel': 'لون الشارات',
  'merchant.store.form.colorBadgeHint': 'يعتمد على اللون الثانوي إذا تُرك فارغًا.',
  'merchant.store.form.colorPromoLabel': 'لون العروض',
  'merchant.store.form.colorPromoHint': 'يعتمد على لون التمييز إذا تُرك فارغًا.',
  'merchant.store.form.colorVariantLabel': 'لون المتغيرات',
  'merchant.store.form.colorVariantHint':
    'حلقة تحديد المتغير (المقاس، اللون...). يعتمد على لون التمييز إذا تُرك فارغًا.',
  'merchant.store.form.colorReset': 'إعادة تعيين',

  'merchant.store.health.title': 'صحة المتجر',
  'merchant.store.health.storeConfigured': 'المتجر مُهيَّأ',
  'merchant.store.health.storeConfiguredHint': 'أدخل بيانات متجرك في النموذج أعلاه.',
  'merchant.store.health.categoriesPresent': 'الفئات موجودة',
  'merchant.store.health.categoriesPresentHint': 'أنشئ فئة واحدة على الأقل.',
  'merchant.store.health.productsPresent': 'المنتجات موجودة',
  'merchant.store.health.productsPresentHint': 'أنشئ منتجاً واحداً على الأقل.',
  'merchant.store.health.activeProductsHint': 'انشر منتجاً واحداً على الأقل (بحالة «نشط»).',
  'merchant.store.health.variantsPresent': 'المتغيرات موجودة',
  'merchant.store.health.variantsPresentHint': 'أضف متغيراً واحداً على الأقل لمنتج.',
  'merchant.store.health.stockAvailableHint': 'أعلن عن مخزون متاح لمتغير واحد على الأقل.',

  'merchant.store.visibility.label': 'حالة المتجر',
  'merchant.store.visibility.active': 'نشط — مرئي للعموم',
  'merchant.store.visibility.pending': 'بانتظار الموافقة — غير مرئي للعموم بعد',
  'merchant.store.visibility.suspended': 'موقوف — غير مرئي للعموم',
  'merchant.store.visibility.refused': 'مرفوض — غير مرئي للعموم',
  'merchant.share.refused.title': 'تم رفض طلبك',
  'merchant.share.refused.text':
    'لم توافق المنصة على هذا المتجر: لن يكون ظاهرًا للعموم. تواصل مع دعم المنصة لمعرفة المزيد.',
  'merchant.store.visibility.publicCatalog': 'الكتالوج العام:',
  'merchant.share.readyTitle': 'متجرك أصبح متاحًا على الإنترنت',
  'merchant.share.myShop': 'متجري',
  'merchant.share.online': 'متاح',
  'merchant.share.sendRow': 'إرسال',
  'merchant.share.trackedRow': 'رابط متتبَّع',
  'merchant.share.readySubtitle':
    'شارك هذا الرابط مع زبائنك: على واتساب أو فيسبوك أو تيليغرام أو أي مكان آخر.',
  'merchant.share.linkLabel': 'الرابط العام لمتجرك',
  'merchant.share.copy': 'نسخ الرابط',
  'merchant.share.copied': 'تم نسخ الرابط',
  'merchant.share.copyFailed': 'تعذّر النسخ: حدّد الرابط وانسخه يدويًا.',
  'merchant.share.open': 'فتح متجري',
  'merchant.share.newTab': '(يفتح في تبويب جديد)',
  'merchant.share.shareOn': 'مشاركة عبر',
  'merchant.share.whatsapp': 'واتساب',
  'merchant.share.facebook': 'فيسبوك',
  'merchant.share.telegram': 'تيليغرام',
  'merchant.share.message': 'اكتشف متجري {{name}}:',
  'merchant.share.noStore.title': 'أنشئ متجرك لتحصل على رابطك العام',
  'merchant.share.noStore.text': 'أدخل اسم متجرك وعنوانه: سيظهر هنا الرابط الجاهز للمشاركة.',
  'merchant.share.noStore.cta': 'إنشاء متجري',
  'merchant.share.pending.title': 'متجرك في انتظار التفعيل',
  'merchant.share.pending.text': 'بمجرد تفعيله سيظهر رابطك العام هنا جاهزًا للمشاركة.',
  'merchant.share.suspended.title': 'متجرك معلّق',
  'merchant.share.suspended.text': 'لم يعد ظاهرًا للعموم. تواصل مع دعم المنصة لإعادة تفعيله.',
  'merchant.share.noProduct.title': 'أضف أول منتج لتجعل المتجر متاحًا على الإنترنت',
  'merchant.share.noProduct.text': 'سيظهر رابطك العام هنا بمجرد أن يصبح منتج ظاهرًا في الكتالوج.',
  'merchant.share.noProduct.cta': 'إضافة منتج',
  'merchant.share.unpublished.title': 'انشر كتالوجك لتجعل المتجر متاحًا على الإنترنت',
  'merchant.share.unpublished.text':
    'منتجاتك ما زالت مسودات أو غير مفعّلة. اجعل منتجًا واحدًا على الأقل «نشطًا» وسيظهر رابطك العام هنا.',
  'merchant.share.unpublished.cta': 'عرض منتجاتي',

  // ---- merchant: categories ----
  'merchant.categories.subtitle': 'نظّم كتالوجك في فئات وفئات فرعية، مرئية في متجرك العام.',
  'merchant.categories.newCategory': 'فئة جديدة',
  'merchant.categories.loadError': 'تعذر تحميل الفئات',
  'merchant.categories.accessDeniedDescription': 'حسابك لا يملك صلاحية إدارة الكتالوج.',
  'merchant.categories.emptyTitle': 'لا توجد فئات حتى الآن',
  'merchant.categories.emptyDescription':
    'أنشئ فئتك الأولى لتبدأ بتنظيم كتالوجك (مثال: أزياء، إلكترونيات، منزل).',
  'merchant.categories.editTitle': 'تعديل الفئة',
  'merchant.categories.createAction': 'إنشاء',
  'merchant.categories.updated': 'تم تحديث الفئة.',
  'merchant.categories.created': 'تم إنشاء الفئة.',
  'merchant.categories.nextStep.title': 'تم إنشاء الفئة',
  'merchant.categories.nextStep.description': 'الخطوة التالية الموصى بها: أنشئ أول منتج.',
  'merchant.categories.nextStep.cta': 'إنشاء منتج',
  'merchant.categories.deleted': 'تم حذف الفئة.',
  'merchant.categories.deleteConfirm': 'حذف الفئة «{{name}}»؟ هذا الإجراء لا رجعة فيه.{{warning}}',
  'merchant.categories.deleteWarning':
    ' {{count}} منتج(ات) مرتبطة حالياً بهذه الفئة وستفقد هذا الارتباط.',
  'merchant.categories.form.nameLabel': 'الاسم',
  'merchant.categories.form.slugHint': 'يُستخدم في الرابط العام للفئة.',
  'merchant.categories.form.imageLabel': 'الصورة',
  'merchant.categories.form.imageHint': 'رابط لصورة مستضافة مسبقاً (لا رفع ملفات هنا).',
  'merchant.categories.form.parentLabel': 'الفئة الأصل',
  'merchant.categories.form.parentHint': 'اتركه فارغاً لفئة جذرية.',
  'merchant.categories.form.activeCheckbox': 'فئة نشطة (مرئية في المتجر العام)',
  'merchant.categories.table.colParent': 'الأصل',
  'merchant.categories.table.colProducts': 'المنتجات',
  'merchant.categories.active': 'نشطة',
  'merchant.categories.inactive': 'غير نشطة',

  // ---- merchant: products ----
  'merchant.products.subtitle': 'أدر كتالوج متجرك.',
  'merchant.products.newProduct': 'منتج جديد',
  'merchant.products.loadError': 'تعذر تحميل المنتجات',
  'merchant.products.emptyTitle': 'لا توجد منتجات حتى الآن',
  'merchant.products.emptyDescription': 'أنشئ منتجك الأول لتبدأ بناء كتالوجك.',
  'merchant.products.createAction': 'إنشاء منتج',
  'merchant.products.noMatchTitle': 'لا يوجد منتج يطابق هذه الفلاتر',
  'merchant.products.noMatchDescription': 'جرّب بحثاً آخر أو أعد ضبط الفلاتر.',
  'merchant.products.table.colProduct': 'المنتج',
  'merchant.products.table.colPrice': 'السعر',
  'merchant.products.table.colStock': 'المخزون',
  'merchant.products.table.colCategory': 'الفئة',
  'merchant.products.table.colLastModified': 'آخر تعديل',
  'merchant.products.table.colFeatured': 'اختيار المتجر',
  'merchant.products.featured.add': 'إضافة إلى اختيارات المتجر',
  'merchant.products.featured.remove': 'إزالة من اختيارات المتجر',
  'merchant.products.featured.hint':
    'تُعرض اختيارات المتجر (5 كحدّ أقصى) في أعلى الصفحة الرئيسية للمتجر، ويظهر آخر منتج تمّ اختياره بحجم كبير.',
  'merchant.products.featured.inactiveNote':
    'هذا المنتج غير مفعّل: لن يظهر ضمن اختيارات المتجر إلا بعد تفعيله.',
  'merchant.products.featured.limitReached':
    'لديك بالفعل {{limit}} اختيارات. أزل واحدًا قبل إضافة آخر.',
  'merchant.products.featured.error': 'تعذّر تعديل هذا الاختيار. حاول مرة أخرى.',
  'merchant.products.status.draft': 'مسودة',
  'merchant.products.status.active': 'نشط',
  'merchant.products.status.inactive': 'غير نشط',
  'merchant.products.toolbar.searchPlaceholder': 'البحث بالاسم أو SKU…',
  'merchant.products.toolbar.searchAria': 'البحث عن منتج',
  'merchant.products.toolbar.filterStatusAria': 'تصفية حسب الحالة',
  'merchant.products.toolbar.sortAria': 'ترتيب',
  'merchant.products.toolbar.statusAll': 'كل الحالات',
  'merchant.products.toolbar.sortUpdatedDesc': 'آخر تعديل (الأحدث)',
  'merchant.products.toolbar.sortUpdatedAsc': 'آخر تعديل (الأقدم)',
  'merchant.products.toolbar.sortNameAsc': 'الاسم (أ→ي)',
  'merchant.products.toolbar.sortNameDesc': 'الاسم (ي→أ)',
  'merchant.products.toolbar.sortPriceAsc': 'السعر (تصاعدي)',
  'merchant.products.toolbar.sortPriceDesc': 'السعر (تنازلي)',
  'merchant.products.toolbar.sortStockAsc': 'المخزون (النافد أولاً)',
  'merchant.products.toolbar.sortStockDesc': 'المخزون (المتاح أولاً)',
  'merchant.products.stock.inStock': 'متوفر',
  'merchant.products.stock.lowStock': 'مخزون منخفض',
  'merchant.products.stock.outOfStock': 'نفد المخزون',
  'merchant.products.stock.unavailable': 'غير متاح',
  'merchant.products.stock.units': '{{count}} وحدة',
  'merchant.products.create.title': 'منتج جديد',
  'merchant.products.create.subtitle': 'أضف منتجاً إلى كتالوجك.',
  'merchant.products.create.submitLabel': 'إنشاء المنتج',
  'merchant.products.createdToast': 'تم إنشاء المنتج «{{name}}».',
  'merchant.products.nextStep.title': 'تم إنشاء المنتج بنجاح',
  'merchant.products.nextStep.description': 'الخطوة التالية الموصى بها: تهيئة المخزون.',
  'merchant.products.nextStep.cta': 'الانتقال إلى المخزون',
  'merchant.products.updatedToast': 'تم تحديث المنتج.',
  'merchant.products.deleteConfirm': 'حذف هذا المنتج؟ هذا الإجراء لا رجعة فيه.',
  'merchant.products.deletedToast': 'تم حذف المنتج.',
  'merchant.products.notFoundTitle': 'المنتج غير موجود',
  'merchant.products.detailLoadError': 'تعذر تحميل هذا المنتج',
  'merchant.products.imagesHeading': 'الصور',
  'merchant.products.imageUploaded': 'تم إرسال الصورة.',

  'merchant.products.form.generalInfo': 'معلومات عامة',
  'merchant.products.form.nameLabel': 'اسم المنتج',
  'merchant.products.form.slugHint': 'يُستخدم في الرابط العام للمنتج.',
  'merchant.products.form.skuLabel': 'SKU',
  'merchant.products.form.categoryLabel': 'الفئة',
  'merchant.products.form.categoryLoading': 'جارٍ تحميل الفئات…',
  'merchant.products.form.categoryNone': 'لا فئة',
  'merchant.products.form.shortDescriptionLabel': 'وصف قصير',
  'merchant.products.form.longDescriptionLabel': 'وصف مفصّل',
  'merchant.products.form.brandLabel': 'العلامة التجارية',
  'merchant.products.form.priceAndStatus': 'السعر والحالة',
  'merchant.products.form.priceLabel': 'السعر (دينار)',
  'merchant.products.form.compareAtPriceLabel': 'السعر المشطوب',
  'merchant.products.form.compareAtPriceHint': 'سعر المقارنة المعروض مشطوباً.',
  'merchant.products.form.statusDraftOption': 'مسودة (غير مرئية للعموم)',
  'merchant.products.form.statusActiveOption': 'نشط (مرئي في الكتالوج العام)',

  'merchant.products.images.sending': 'جارٍ الإرسال…',
  'merchant.products.images.error': 'خطأ',
  'merchant.products.images.uploadFailed': 'فشل الإرسال.',
  'merchant.products.images.dropzone': 'اسحب وأفلت صورة، أو انقر للتصفح',
  'merchant.products.images.formats': 'JPEG أو PNG أو WebP',
  'merchant.products.images.loadError': 'تعذر تحميل الصور',
  'merchant.products.images.empty': 'لم يتم إرسال أي صورة لهذا المنتج.',
  'merchant.products.images.loadErrorBadge': 'فشل التحميل',
  'merchant.products.images.position': 'الموضع {{n}}',

  'merchant.products.variants.title': 'المتغيرات',
  'merchant.products.variants.add': 'إضافة متغير',
  'merchant.products.variants.createdToast': 'تم إنشاء المتغير «{{sku}}».',
  'merchant.products.variants.deleteConfirm': 'حذف هذا المتغير؟',
  'merchant.products.variants.deletedToast': 'تم حذف المتغير.',
  'merchant.products.variants.loadError': 'تعذر تحميل المتغيرات',
  'merchant.products.variants.empty': 'لا يوجد متغير لهذا المنتج',
  'merchant.products.variants.colAttributes': 'الخصائص',
  'merchant.products.variants.colSpecificPrice': 'سعر خاص',
  'merchant.products.variants.skuLabel': 'SKU المتغير',
  'merchant.products.variants.priceOverrideHint': 'اتركه فارغاً لاستخدام سعر المنتج.',
  'merchant.products.variants.attributesExample': '(مثال: اللون، المقاس)',
  'merchant.products.variants.attributeKeyPlaceholder': 'المفتاح (مثال: اللون)',
  'merchant.products.variants.attributeValuePlaceholder': 'القيمة (مثال: أسود)',
  'merchant.products.variants.removeAttribute': 'إزالة',
  'merchant.products.variants.addAttribute': 'إضافة خاصية',
  'merchant.products.variants.colStatus': 'الحالة',
  'merchant.products.variants.status.active': 'نشطة',
  'merchant.products.variants.status.inactive': 'معطّلة',
  'merchant.products.variants.activate': 'تفعيل',
  'merchant.products.variants.deactivate': 'تعطيل',
  'merchant.products.variants.activatedToast': 'تم تفعيل النسخة — أصبحت ظاهرة في المتجر من جديد.',
  'merchant.products.variants.deactivatedToast':
    'تم تعطيل النسخة — أصبحت مخفية في المتجر، مع الاحتفاظ بالمخزون والسجل.',

  'merchant.products.options.title': 'خيارات المنتج',
  'merchant.products.options.add': 'إضافة خيار',
  'merchant.products.options.namePlaceholder': 'مثال: المقاس، اللون',
  'merchant.products.options.displayTypeLabel': 'نوع العرض',
  'merchant.products.options.displayType.button': 'أزرار',
  'merchant.products.options.displayType.colorSwatch': 'عيّنات ألوان',
  'merchant.products.options.rename': 'إعادة التسمية',
  'merchant.products.options.delete': 'حذف الخيار',
  'merchant.products.options.deleteConfirm':
    'حذف هذا الخيار؟ غير ممكن إذا كانت نسخة ما زالت تستخدمه.',
  'merchant.products.options.addValue': 'إضافة قيمة',
  'merchant.products.options.valuePlaceholder': 'مثال: 42، أحمر',
  'merchant.products.options.swatchColor': 'لون الدائرة',
  // ---- merchant assistant (chatbot) ----
  'merchant.assistant.open': 'فتح المساعد',
  'merchant.assistant.title': 'المساعد',
  'merchant.assistant.subtitle': 'أدر متجرك عبر المحادثة',
  'merchant.assistant.close': 'إغلاق المساعد',
  'merchant.assistant.newConversation': 'محادثة جديدة',
  'merchant.assistant.unavailable': 'المساعد غير مُعدّ على هذا الخادم. اتصل بمسؤول المنصة.',
  'merchant.assistant.welcome':
    'مرحبًا! أخبرني بما تريد فعله: إنشاء منتج، تعديل المخزون، متابعة طلب، إنشاء قسيمة… سأطلب منك التأكيد قبل أي تعديل.',
  'merchant.assistant.privacy':
    'تتم معالجة رسائلك وبيانات المتجر اللازمة للإجابة بواسطة خدمة ذكاء اصطناعي خارجية (Anthropic). لا تكتب هنا كلمة مرور أبدًا.',
  'merchant.assistant.placeholder': 'اكتب طلبك…',
  'merchant.assistant.send': 'إرسال',
  'merchant.assistant.thinking': 'المساعد يفكر…',
  'merchant.assistant.operation.succeeded': 'تمت بنجاح',
  'merchant.assistant.operation.failed': 'فشلت (الحالة {{status}})',
  'merchant.assistant.secret.hint': 'دوّنها الآن: لن تُعرض مرة أخرى. ولا تُرسل إلى المساعد.',
  'merchant.assistant.secret.copy': 'نسخ',
  'merchant.assistant.secret.copied': 'تم النسخ',
  'merchant.assistant.action.openPage': 'فتح الصفحة',
  'merchant.assistant.action.uploadPhoto': 'اختيار صورة',
  'merchant.assistant.action.uploadLogo': 'اختيار الشعار',
  'merchant.assistant.action.uploadBanner': 'اختيار اللافتة',
  'merchant.assistant.action.uploaded': 'تم إرسال الصورة.',
  'merchant.assistant.action.downloadInvoice': 'تنزيل الفاتورة (PDF)',
  'merchant.assistant.pending.title': 'بانتظار التأكيد',
  'merchant.assistant.pending.confirm': 'تأكيد',
  'merchant.assistant.pending.cancel': 'إلغاء',
  'merchant.assistant.notice.refusal': 'لا يستطيع المساعد الإجابة عن هذا الطلب.',
  'merchant.assistant.notice.outputLimit': 'انقطعت الإجابة؛ لم يُنفَّذ أي شيء.',
  'merchant.assistant.notice.stepLimit': 'هذا الطلب طويل جدًا: قسّمه إلى عدة خطوات.',
  'merchant.assistant.notice.conversationFull': 'هذه المحادثة ممتلئة: ابدأ محادثة جديدة.',
  'merchant.assistant.notice.declined': 'أُلغيت العملية: لم يتغير أي شيء.',
  'merchant.assistant.notice.conversationExpired':
    'انتهت صلاحية هذه المحادثة: الرسالة التالية تبدأ محادثة جديدة.',
  'merchant.assistant.notice.nothingPending':
    'لم يعد هناك ما يجب تأكيده: تمت معالجة هذا الاقتراح أو انتهت صلاحيته.',
  'merchant.products.options.editValue': 'تعديل',
  'merchant.products.options.deleteValue': 'حذف',
  'merchant.products.options.deleteValueConfirm':
    'حذف هذه القيمة؟ غير ممكن إذا كانت نسخة ما زالت تستخدمها.',
  'merchant.products.options.empty': 'لا توجد خيارات بعد — أضف واحدًا لتوليد النسخ.',
  'merchant.products.options.createdToast': 'تم إنشاء الخيار "{{name}}".',
  'merchant.products.options.deletedToast': 'تم حذف الخيار.',
  'merchant.products.options.valueCreatedToast': 'تمت إضافة القيمة "{{value}}".',
  'merchant.products.options.valueDeletedToast': 'تم حذف القيمة.',
  'merchant.products.options.autoGeneratedBadge': 'مُجمّعة تلقائيًا',
  'merchant.products.options.loadError': 'تعذّر تحميل الخيارات',

  'merchant.products.generator.title': 'مولّد النسخ',
  'merchant.products.generator.openButton': 'توليد النسخ',
  'merchant.products.generator.selectValues': 'اختر القيم المراد دمجها لكل خيار.',
  'merchant.products.generator.stepSelect': 'الاختيار',
  'merchant.products.generator.stepPreview': 'المعاينة',
  'merchant.products.generator.previewTitle': 'التركيبات المراد إنشاؤها',
  'merchant.products.generator.colCombination': 'التركيبة',
  'merchant.products.generator.colSku': 'SKU',
  'merchant.products.generator.colPrice': 'سعر خاص',
  'merchant.products.generator.noOptions': 'أنشئ خيارًا واحدًا على الأقل بقيم قبل توليد النسخ.',
  'merchant.products.generator.noCombinations': 'اختر قيمة واحدة على الأقل لكل خيار.',
  'merchant.products.generator.next': 'معاينة',
  'merchant.products.generator.back': 'رجوع',
  'merchant.products.generator.generate': 'إنشاء {{count}} نسخة',
  'merchant.products.generator.generatedToast': 'تم إنشاء {{count}} نسخة.',
  'merchant.products.generator.duplicateSkuHint': 'يجب أن يكون كل SKU فريدًا داخل المتجر.',

  'merchant.products.bulkEdit.title': 'تعديل جماعي',
  'merchant.products.bulkEdit.openButton': 'تعديل جماعي',
  'merchant.products.bulkEdit.selectAll': 'تحديد الكل',
  'merchant.products.bulkEdit.selectedCount': '{{count}} نسخة محددة',
  'merchant.products.bulkEdit.noSelection': 'اختر نسخة واحدة على الأقل.',
  'merchant.products.bulkEdit.priceModeLabel': 'السعر',
  'merchant.products.bulkEdit.priceMode.none': 'بدون تغيير',
  'merchant.products.bulkEdit.priceMode.relative': 'تعديل نسبي (+/-)',
  'merchant.products.bulkEdit.priceMode.absolute': 'سعر ثابت جديد',
  'merchant.products.bulkEdit.priceAmountLabel': 'المبلغ',
  'merchant.products.bulkEdit.stockDeltaLabel': 'تعديل المخزون',
  'merchant.products.bulkEdit.stockDeltaHint': 'رقم موجب للتزويد، وسالب للسحب.',
  'merchant.products.bulkEdit.thresholdLabel': 'عتبة المخزون المنخفض',
  'merchant.products.bulkEdit.statusLabel': 'الحالة',
  'merchant.products.bulkEdit.status.unchanged': 'بدون تغيير',
  'merchant.products.bulkEdit.status.activate': 'تفعيل',
  'merchant.products.bulkEdit.status.deactivate': 'تعطيل',
  'merchant.products.bulkEdit.reasonLabel': 'السبب (اختياري)',
  'merchant.products.bulkEdit.apply': 'تطبيق',
  'merchant.products.bulkEdit.appliedToast': 'تم تحديث {{count}} نسخة.',

  'merchant.products.reviewBanner.title': 'راجع خياراتك',
  'merchant.products.reviewBanner.description':
    'قمنا تلقائيًا بتجميع خصائص نسخك الحالية في خيارات منظمة. راجعها عندما تشاء — تبقى نسخك قابلة للبيع خلال ذلك.',
  'merchant.products.reviewBanner.dismiss': 'لقد راجعتها',
  'merchant.products.reviewBanner.dismissedToast': 'تم وضع علامة على الخيارات كمُراجعة.',

  // ---- merchant: inventory ----
  'merchant.inventory.subtitle': 'المخزون المتاح وحدود التنبيه لجميع منتجاتك.',
  'merchant.inventory.loadError': 'تعذر تحميل الجرد',
  'merchant.inventory.emptyTitle': 'لا توجد منتجات لعرضها',
  'merchant.inventory.emptyDescription': 'أضف منتجاً لتبدأ بتتبع مخزونه هنا.',
  'merchant.inventory.table.colStockAvailable': 'المخزون المتاح',
  'merchant.inventory.table.colAlertThreshold': 'حد التنبيه',
  'merchant.inventory.none': 'لا شيء',
  'merchant.inventory.adjust': 'تعديل',
  'merchant.inventory.drawer.title': 'تعديل المخزون — {{name}}{{skuSuffix}}',
  'merchant.inventory.drawer.adjustmentLabel': 'التعديل',
  'merchant.inventory.drawer.adjustmentHint': 'موجب لإضافة مخزون، سالب لسحبه (مثال: -5).',
  'merchant.inventory.drawer.reasonPlaceholder': 'مثال: استلام من المورّد',
  'merchant.inventory.drawer.thresholdLabel': 'الحد الأدنى للتنبيه',
  'merchant.inventory.drawer.thresholdHint': 'اتركه فارغاً للاحتفاظ بالحد الحالي.',
  'merchant.inventory.drawer.apply': 'تطبيق',
  'merchant.inventory.adjustedToast': 'تم تعديل المخزون: {{available}} متاح، {{reserved}} محجوز.',

  // ---- admin: shared layout ----
  'admin.sidebar.subtitle': 'إدارة المنصة',
  'admin.topbar.label': 'مساحة مسؤول المنصة',

  // ---- admin: platform accounts (lot B1) ----
  'admin.nav.administrators': 'المسؤولون',
  'admin.nav.dashboard': 'لوحة القيادة',
  'admin.dashboard.title': 'لوحة القيادة',
  'admin.dashboard.subtitle': 'المنصة في لمحة: النشاط والإيرادات والتجار والمسؤولون والأمان.',
  'admin.dashboard.updatedNow': 'تم التحديث للتو',
  'admin.dashboard.updatedOneSecond': 'تم التحديث قبل ثانية واحدة',
  'admin.dashboard.updatedSeconds': 'تم التحديث قبل {{seconds}} ثانية',
  'admin.dashboard.updatedMinutes': 'تم التحديث قبل {{minutes}} دقيقة',
  'admin.dashboard.refresh': 'تحديث',
  'admin.dashboard.refreshing': 'جارٍ التحديث…',
  'admin.dashboard.cacheNote': 'تُعاد حسابات الأرقام مرة كل 60 ثانية على الأكثر.',
  'admin.dashboard.loading': 'جارٍ تحميل لوحة القيادة…',
  'admin.dashboard.error.title': 'تعذّر تحميل لوحة القيادة',
  'admin.dashboard.error.forbidden': 'هذه اللوحة مخصّصة للمسؤول الأعلى فقط.',
  'admin.dashboard.error.text': 'أعد المحاولة بعد قليل. إذا استمرت المشكلة فتواصل مع فريق التشغيل.',
  'admin.dashboard.retry': 'إعادة المحاولة',
  'admin.dashboard.empty.title': 'لا توجد بيانات بعد',
  'admin.dashboard.empty.text': 'ستظهر الأرقام بمجرد أن يكون لدى المنصة تجار وطلبات.',
  'admin.dashboard.chartLoadError': 'المنحنيات غير متاحة حاليًا.',
  'admin.dashboard.section.financial': 'المالية',
  'admin.dashboard.section.merchants': 'التجار',
  'admin.dashboard.section.actions': 'يتطلب إجراءً',
  'admin.dashboard.section.trends': 'التطور',
  'admin.dashboard.section.admins': 'المسؤولون',
  'admin.dashboard.section.quality': 'جودة البيانات',
  'admin.dashboard.section.security': 'الأمان',
  'admin.dashboard.section.products': 'المنتجات',
  'admin.dashboard.section.topMerchants': 'أفضل التجار حسب رقم الأعمال',
  'admin.dashboard.fin.salesTotal': 'إجمالي المبيعات',
  'admin.dashboard.fin.revenueToday': 'رقم أعمال اليوم',
  'admin.dashboard.fin.revenueMonth': 'رقم أعمال الشهر',
  'admin.dashboard.fin.revenueYear': 'رقم أعمال السنة',
  'admin.dashboard.fin.averageBasket': 'متوسط السلة',
  'admin.dashboard.fin.ordersTotal': 'الطلبات (الإجمالي)',
  'admin.dashboard.fin.ordersToday': 'طلبات اليوم',
  'admin.dashboard.fin.excludesCancelled': 'دون الطلبات الملغاة',
  'admin.dashboard.fin.cancelled': '{{count}} طلب(ات) ملغاة غير محتسبة',
  'admin.dashboard.mer.total': 'إجمالي التجار',
  'admin.dashboard.mer.active': 'نشطون',
  'admin.dashboard.mer.pending': 'قيد الانتظار',
  'admin.dashboard.mer.suspended': 'موقوفون',
  'admin.dashboard.mer.unassigned': 'غير مُسنَدين',
  'admin.dashboard.mer.openList': 'فتح القائمة',
  'admin.dashboard.mer.withStore': '{{count}} لديهم متجر على الإنترنت',
  'admin.dashboard.mer.refused': '{{count}} مرفوض',
  'admin.dashboard.adm.total': 'المسؤولون',
  'admin.dashboard.adm.active': 'نشطون',
  'admin.dashboard.adm.disabled': 'معطّلون',
  'admin.dashboard.adm.topTitle': 'أفضل 5 مسؤولين حسب عدد التجار',
  'admin.dashboard.adm.allTitle': 'جميع المسؤولين',
  'admin.dashboard.adm.showAll': 'عرض جميع المسؤولين ({{count}})',
  'admin.dashboard.adm.showTop': 'عرض أفضل 5',
  'admin.dashboard.adm.capped':
    'تُعرض قائمة {{shown}} من المسؤولين الذين يديرون أكبر عدد من المتاجر، من أصل {{total}}.',
  'admin.dashboard.adm.cappedLink': 'عرض جميع المسؤولين',
  'admin.dashboard.adm.colAdmin': 'المسؤول',
  'admin.dashboard.adm.colMerchants': 'التجار',
  'admin.dashboard.adm.colActive': 'نشطة',
  'admin.dashboard.adm.colPending': 'قيد الانتظار',
  'admin.dashboard.adm.colSuspended': 'موقوفة',
  'admin.dashboard.adm.colRevenue': 'رقم الأعمال',
  'admin.dashboard.adm.colOrders': 'الطلبات',
  'admin.dashboard.adm.none': 'لا يوجد مسؤول منصة مسجَّل.',
  'admin.dashboard.adm.disabledBadge': 'معطّل',
  'admin.dashboard.qua.neverSignedIn': 'تجار لم يسجلوا الدخول قط',
  'admin.dashboard.qua.neverSignedInHint': 'لم يفتح المالك مساحته بعد.',
  'admin.dashboard.qua.provisionalExpired': 'كلمات مرور مؤقتة منتهية',
  'admin.dashboard.qua.provisionalExpiredHint': 'يجدّدها المسؤول المسيّر أو المسؤول الأعلى.',
  'admin.dashboard.qua.withoutOwner': 'متاجر بلا مالك',
  'admin.dashboard.qua.withoutOwnerHint': 'لا يوجد حساب مالك نشط مرتبط.',
  'admin.dashboard.qua.withoutProduct': 'متاجر بلا منتجات',
  'admin.dashboard.qua.withoutProductHint': 'دون المتاجر المرفوضة.',
  'admin.dashboard.sec.superadmins': 'المسؤولون الأعلى',
  'admin.dashboard.sec.platformAdmins': 'مسؤولو المنصة',
  'admin.dashboard.sec.logins': 'تسجيلات الدخول اليوم',
  'admin.dashboard.sec.loginsHint': 'منها حسابات المنصة: {{count}}',
  'admin.dashboard.sec.failed': 'محاولات فاشلة اليوم',
  'admin.dashboard.sec.failedHint':
    'منها حسابات المنصة: {{count}} · محجوبة بالتحديد: {{throttled}}',
  'admin.dashboard.sec.locked': 'حسابات مقفلة',
  'admin.dashboard.sec.promotions': 'ترقيات قيد الانتظار',
  'admin.dashboard.act.unassigned': 'متاجر غير مُسنَدة',
  'admin.dashboard.act.unassignedHint': 'تُسنَد إلى مسؤول قبل أي اعتماد.',
  'admin.dashboard.act.pendingOver7d': 'متاجر قيد الانتظار > 7 أيام',
  'admin.dashboard.act.pendingOver7dHint': 'أُنشئت منذ أكثر من سبعة أيام ولم تُعتمد بعد.',
  'admin.dashboard.act.reassignments': 'إعادات الإسناد الأخيرة',
  'admin.dashboard.act.reassignmentsHint': '{{last7d}} خلال 7 أيام · {{last30d}} خلال 30 يومًا',
  'admin.dashboard.act.toHandleTitle': 'متاجر تتطلب إجراءً',
  'admin.dashboard.act.toHandleTotal': 'عرض {{shown}} من {{total}}',
  'admin.dashboard.act.colShop': 'المتجر',
  'admin.dashboard.act.colStatus': 'الحالة',
  'admin.dashboard.act.colReason': 'المطلوب',
  'admin.dashboard.act.colAge': 'العمر',
  'admin.dashboard.act.colManager': 'المسؤول المسيّر',
  'admin.dashboard.act.reason.unassigned': 'إسناد إلى مسؤول',
  'admin.dashboard.act.reason.provisional_expired': 'تجديد كلمة المرور المؤقتة',
  'admin.dashboard.act.reason.pending_over_7d': 'اعتماد أو رفض (قيد الانتظار منذ أكثر من 7 أيام)',
  'admin.dashboard.act.ageDays': '{{days}} ي',
  'admin.dashboard.act.noneToHandle': 'لا شيء يتطلب إجراءً: جميع المتاجر مُسنَدة ومحدَّثة.',
  'admin.dashboard.act.unnamed': 'بلا اسم',
  'admin.dashboard.act.noManager': 'لا أحد',
  'admin.dashboard.act.recentTitle': 'آخر إعادات الإسناد',
  'admin.dashboard.act.recentRow': '{{name}} ← {{admin}}',
  'admin.dashboard.act.recentNone': 'لا توجد إعادة إسناد حديثة.',
  'admin.dashboard.act.openPortfolios': 'فتح المحافظ',
  'admin.dashboard.trend.sales': 'المبيعات',
  'admin.dashboard.trend.orders': 'الطلبات',
  'admin.dashboard.trend.registrations': 'تجار جدد',
  'admin.dashboard.trend.currency': 'د.ت',
  'admin.dashboard.trend.range': 'الفترة',
  'admin.dashboard.trend.r7': '7 أيام',
  'admin.dashboard.trend.r30': '30 يومًا',
  'admin.dashboard.trend.r90': '90 يومًا',
  'admin.dashboard.trend.r12m': '12 شهرًا',
  'admin.dashboard.trend.aria': '{{title}}: {{total}} خلال الفترة، من {{from}} إلى {{to}}',
  'admin.dashboard.trend.empty': 'لا توجد بيانات خلال الفترة.',
  'admin.dashboard.trend.peak': 'الذروة: {{value}} بتاريخ {{date}}',
  'admin.dashboard.trend.tableCaption': 'بيانات منحنى «{{title}}»',
  'admin.dashboard.trend.colPeriod': 'الفترة',
  'admin.dashboard.pro.total': 'المنتجات',
  'admin.dashboard.pro.active': 'نشطة',
  'admin.dashboard.pro.outOfStock': 'نفدت من المخزون',
  'admin.dashboard.pro.topTitle': 'أكثر المنتجات مبيعًا',
  'admin.dashboard.pro.colProduct': 'المنتج',
  'admin.dashboard.pro.colMerchant': 'التاجر',
  'admin.dashboard.pro.colQuantity': 'الكمية',
  'admin.dashboard.pro.colRevenue': 'رقم الأعمال',
  'admin.dashboard.pro.none': 'لم يُبَع أي منتج بعد.',
  'admin.dashboard.top.colMerchant': 'التاجر',
  'admin.dashboard.top.colRevenue': 'رقم الأعمال',
  'admin.dashboard.top.colOrders': 'الطلبات',
  'admin.dashboard.top.colManager': 'المسؤول المسيّر',
  'admin.dashboard.top.none': 'لا مبيعات بعد.',
  'admin.accounts.title': 'مسؤولو المنصة',
  'admin.accounts.subtitle':
    'من يملك صلاحيات على المنصة. إنشاء مسؤول، سحب دور، تعطيل حساب، وترقية مشرف أعلى بموافقة شخصين.',
  'admin.accounts.tab.label': 'أقسام المسؤولين',
  'admin.accounts.tab.accounts': 'الحسابات',
  'admin.accounts.tab.create': 'إنشاء مسؤول',
  'admin.accounts.tab.promotions': 'ترقيات المشرف الأعلى',
  'admin.accounts.tab.lookup': 'انتماءات حساب',
  'admin.accounts.reasonPlaceholder': 'مثال: المراجعة الفصلية لصلاحيات المسؤولين',
  'admin.accounts.reasonRequiredDescription': 'اذكر سبب الاطلاع على هذه القائمة: الاطلاع مُدقَّق.',
  'admin.accounts.loadError': 'تعذّر تحميل الحسابات.',
  'admin.accounts.empty': 'لا يوجد حساب على مستوى المنصة.',
  'admin.accounts.table.caption': 'حسابات المنصة',
  'admin.accounts.table.colAccount': 'الحساب',
  'admin.accounts.table.colRole': 'الدور',
  'admin.accounts.table.colStatus': 'الحالة',
  'admin.accounts.table.colLastLogin': 'آخر تسجيل دخول',
  'admin.accounts.table.neverSignedIn': 'لم يسجّل الدخول قط',
  'admin.accounts.role.superadmin': 'مشرف أعلى',
  'admin.accounts.role.platform_admin': 'مسؤول',
  'admin.accounts.status.active': 'نشط',
  'admin.accounts.status.disabled': 'معطّل',
  'admin.accounts.status.locked': 'مقفل',
  'admin.accounts.provisionalPending': 'كلمة مرور مؤقتة قيد الانتظار',
  'admin.accounts.you': 'أنت',
  'admin.accounts.action.revoke': 'سحب الدور',
  'admin.accounts.action.disable': 'تعطيل',
  'admin.accounts.action.enable': 'إعادة التفعيل',
  'admin.accounts.revokeTitle': 'سحب دور المنصة',
  'admin.accounts.revokeWarning':
    'يفقد الحساب كل صلاحياته على المنصة وتُغلق جلساته. لا يُحذف الحساب نفسه. لا يمكن أبدًا سحب دور آخر مشرف أعلى. لا يمكن سحب دور مسؤول لا يزال يدير تجاراً ولا تعطيله: انقل محفظته أولاً (قائمة المحافظ).',
  'admin.accounts.disableTitle': 'تعطيل الحساب',
  'admin.accounts.disableWarning':
    'لا يمكن للشخص تسجيل الدخول بعد الآن وتُغلق جلساته. يمكن التراجع. لا يمكن أبدًا تعطيل آخر مشرف أعلى نشط. لا يمكن سحب دور مسؤول لا يزال يدير تجاراً ولا تعطيله: انقل محفظته أولاً (قائمة المحافظ).',
  'admin.accounts.enableTitle': 'إعادة تفعيل الحساب',
  'admin.accounts.updatedToast': 'تم تحديث الحساب.',
  'admin.accounts.create.title': 'إنشاء مسؤول',
  'admin.accounts.create.note':
    'يحصل الحساب الجديد على دور المسؤول وليس المشرف الأعلى (ترقية المشرف الأعلى تتم بموافقة شخصين). تُنشأ كلمة مرور مؤقتة وتظهر مرة واحدة فقط.',
  'admin.accounts.create.emailLabel': 'البريد الإلكتروني للمسؤول الجديد',
  'admin.accounts.create.displayNameLabel': 'الاسم المعروض',
  'admin.accounts.create.submit': 'إنشاء المسؤول',
  'admin.accounts.create.success': 'تم إنشاء المسؤول.',
  'admin.accounts.create.createAnother': 'إنشاء مسؤول آخر',
  'admin.accounts.create.handover':
    'سلّم كلمة المرور هذه مباشرة أو عبر قناة آمنة. تنتهي صلاحيتها وعلى الشخص اختيار كلمة جديدة عند أول دخول.',
  'admin.promotions.title': 'الترقية إلى مشرف أعلى',
  'admin.promotions.subtitle':
    'مشرفان أعلى مختلفان: أحدهما يطلب والآخر يؤكد. ثلاثة مشرفين أعلى كحد أقصى. الطلب ينتهي بمرور الوقت.',
  'admin.promotions.requestTitle': 'طلب ترقية',
  'admin.promotions.targetLabel': 'المسؤول المراد ترقيته',
  'admin.promotions.targetPlaceholder': 'اختر مسؤولًا نشطًا',
  'admin.promotions.noCandidates': 'لا يوجد مسؤول نشط للترقية.',
  'admin.promotions.requestSubmit': 'طلب الترقية',
  'admin.promotions.requestedToast': 'تم إرسال الطلب: يجب أن يؤكده مشرف أعلى آخر.',
  'admin.promotions.confirmedToast': 'تم تأكيد الترقية.',
  'admin.promotions.rejectedToast': 'تم رفض الطلب.',
  'admin.promotions.cancelledToast': 'تم إلغاء الطلب.',
  'admin.promotions.listTitle': 'الطلبات الأخيرة (30 يومًا)',
  'admin.promotions.empty': 'لا توجد طلبات حديثة.',
  'admin.promotions.table.caption': 'طلبات الترقية',
  'admin.promotions.table.colTarget': 'الحساب المراد ترقيته',
  'admin.promotions.table.colRequestedBy': 'طلب من',
  'admin.promotions.table.colStatus': 'الحالة',
  'admin.promotions.table.colExpires': 'ينتهي في',
  'admin.promotions.status.pending': 'قيد الانتظار',
  'admin.promotions.status.confirmed': 'مؤكدة',
  'admin.promotions.status.rejected': 'مرفوضة',
  'admin.promotions.status.cancelled': 'ملغاة',
  'admin.promotions.status.expired': 'منتهية',
  'admin.promotions.action.confirm': 'تأكيد',
  'admin.promotions.action.reject': 'رفض',
  'admin.promotions.action.cancel': 'إلغاء طلبي',
  'admin.promotions.confirmTitle': 'تأكيد الترقية',
  'admin.promotions.confirmWarning':
    'سيصبح الحساب مشرفًا أعلى وستُغلق جلساته. أنت تؤكد بصفتك المشرف الثاني: لا يمكنك تأكيد طلبك الخاص.',
  'admin.promotions.rejectTitle': 'رفض الطلب',
  'admin.promotions.cancelTitle': 'إلغاء الطلب',
  'admin.promotions.waitingForOther': 'بانتظار مشرف أعلى آخر',
  'errors.platformAccountSelf': 'لا يمكنك فعل ذلك بحسابك الخاص.',
  'errors.lastSuperadmin': 'لا يمكن سحب دور آخر مشرف أعلى نشط ولا تعطيله.',
  'errors.accountStatusUnchanged': 'الحساب في هذه الحالة بالفعل.',
  'errors.accountHasMerchants': 'لا يزال هذا المسؤول يدير تجارًا: انقل محفظته أولاً.',
  'errors.managerRequired': 'حدّد المسؤول الذي سيدير هذا التاجر.',
  'errors.managerForbidden': 'وحده المشرف الأعلى يمكنه إسناد تاجر إلى مسؤول آخر.',
  'errors.managerInvalid': 'المسؤول المختار ليس مسؤول منصة نشطًا.',
  'errors.portfolioTargetInvalid': 'المسؤول المختار ليس مسؤول منصة نشطًا.',
  'errors.portfolioUnchanged': 'هؤلاء التجار يديرهم هذا المسؤول بالفعل.',
  'errors.portfolioSameAdmin': 'لا يمكن نقل المحفظة إلى مسؤولها نفسه.',
  'errors.portfolioEmpty': 'هذا المسؤول لا يدير أي تاجر.',
  'errors.suspendedBySuperadmin':
    'علّق مشرف أعلى هذا التاجر: وحده المشرف الأعلى يمكنه إعادة تفعيله.',
  'errors.ownerInUse':
    'استخدم المالك حسابه بالفعل: وحده المشرف الأعلى يستطيع إعادة تعيين كلمة المرور الآن.',
  'errors.promotionSelf': 'لا أحد يرقّي نفسه أو يؤكد ترقيته.',
  'errors.promotionTargetNotEligible': 'يمكن ترقية مسؤول نشط فقط إلى مشرف أعلى.',
  'errors.superadminCapReached': 'لا يمكن أن يوجد أكثر من 3 مشرفين أعلى.',
  'errors.noSecondSuperadmin':
    'تتطلب الترقية مشرفًا أعلى ثانيًا لتأكيدها: استخدم إجراء المشغّل بشخصين.',
  'errors.promotionAlreadyPending': 'هناك ترقية بانتظار هذا الحساب بالفعل.',
  'errors.promotionNotPending': 'تمت معالجة هذا الطلب بالفعل.',
  'errors.promotionExpired': 'انتهت صلاحية هذا الطلب.',
  'errors.promotionSamePerson': 'يجب أن يؤكد مشرف أعلى آخر: لا أحد يؤكد طلبه الخاص.',

  // ---- admin: access and passwords (lot B2) ----
  'admin.nav.access': 'الوصول وكلمات المرور',
  'admin.access.title': 'الوصول وكلمات المرور',
  'admin.access.subtitle':
    'مساعدة حساب دون المساس بقاعدة البيانات: الحسابات المقفلة وكلمات المرور المؤقتة والجلسات. كل اطلاع مُدقَّق.',
  'admin.access.tab.label': 'أقسام الوصول وكلمات المرور',
  'admin.access.tab.locked': 'الحسابات المقفلة',
  'admin.access.tab.provisional': 'كلمات المرور المؤقتة',
  'admin.access.tab.lookup': 'البحث عن حساب',
  'admin.access.kind.superadmin': 'مشرف أعلى',
  'admin.access.kind.platform_admin': 'مسؤول',
  'admin.access.kind.shop': 'حساب متجر',
  'admin.access.loadError': 'تعذّر تحميل القائمة.',
  'admin.access.locked.empty': 'لا توجد حسابات مقفلة.',
  'admin.access.locked.caption': 'الحسابات المقفلة',
  'admin.access.col.account': 'الحساب',
  'admin.access.col.kind': 'النوع',
  'admin.access.col.failures': 'محاولات فاشلة',
  'admin.access.col.lockedUntil': 'مقفل حتى',
  'admin.access.action.unlock': 'إلغاء القفل',
  'admin.access.unlockTitle': 'إلغاء قفل الحساب',
  'admin.access.unlockWarning':
    'يُعاد عدّاد المحاولات الفاشلة إلى الصفر ويمكن للشخص تسجيل الدخول فورًا. تحقق من هويته أولًا.',
  'admin.access.unlockedToast': 'تم إلغاء قفل الحساب.',
  'admin.access.provisional.empty': 'لا توجد كلمات مرور مؤقتة قيد الانتظار.',
  'admin.access.provisional.caption': 'كلمات المرور المؤقتة قيد الانتظار',
  'admin.access.col.expires': 'ينتهي في',
  'admin.access.provisional.expired': 'منتهٍ: يجب تجديده',
  'admin.access.provisional.valid': 'قيد الانتظار',
  'admin.access.lookup.emailLabel': 'البريد الإلكتروني للحساب',
  'admin.access.lookup.submit': 'بحث',
  'admin.access.lookup.notFound': 'لا يوجد حساب بهذا العنوان.',
  'admin.access.lookup.reasonFirst': 'اذكر أولًا سبب الاطلاع (مُدقَّق) أعلاه.',
  'admin.access.lookup.status': 'الحالة',
  'admin.access.lookup.lastLogin': 'آخر تسجيل دخول',
  'admin.access.lookup.locked': 'مقفل حتى',
  'admin.access.lookup.provisional': 'كلمة مرور مؤقتة قيد الانتظار',
  'admin.access.sessions.title': 'جلسات الحساب',
  'admin.access.sessions.caption': 'الجلسات',
  'admin.access.sessions.empty': 'لا توجد جلسات.',
  'admin.access.sessions.retention': 'يُحتفظ بعنوان IP والمتصفح لمدة 90 يومًا ثم يُمحيان.',
  'admin.access.sessions.colOpened': 'فُتحت في',
  'admin.access.sessions.colLastUsed': 'آخر نشاط',
  'admin.access.sessions.colIp': 'عنوان IP',
  'admin.access.sessions.colBrowser': 'المتصفح',
  'admin.access.sessions.colState': 'الحالة',
  'admin.access.sessions.active': 'نشطة',
  'admin.access.sessions.ended': 'مغلقة',
  'admin.access.sessions.noMetadata': 'مُمحى (أكثر من 90 يومًا)',
  'admin.access.sessions.reason.logout': 'تسجيل خروج',
  'admin.access.sessions.reason.logout_all': 'تسجيل خروج من كل مكان',
  'admin.access.sessions.reason.token_reuse_detected': 'رُصدت سرقة رمز',
  'admin.access.sessions.reason.password_changed': 'تغيّرت كلمة المرور',
  'admin.access.sessions.reason.expired': 'منتهية',
  'admin.access.sessions.reason.role_changed': 'تغيّر الدور',
  'admin.access.sessions.reason.account_disabled': 'عُطّل الحساب',
  'admin.access.sessions.reason.admin_revoked': 'أغلقها مشرف أعلى',
  'admin.access.sessions.revokeAll': 'إغلاق كل الجلسات',
  'admin.access.sessions.revokeTitle': 'إغلاق كل جلسات الحساب',
  'admin.access.sessions.revokeWarning':
    'يُسجَّل الشخص خروجه من كل مكان وعليه إعادة الدخول. غير ممكن على حسابك الخاص.',
  'admin.access.sessions.revokedToast': 'تم إغلاق {{count}} جلسة.',
  'admin.access.reset.action': 'إعادة تعيين كلمة المرور',
  'admin.access.reset.title': 'كلمة مرور مؤقتة جديدة',
  'admin.access.reset.warning':
    'تتوقف كلمة المرور الحالية عن العمل وتُغلق جلسات الحساب ويُرفع القفل. على الشخص اختيار كلمة مروره عند أول دخول. 3 عمليات إعادة تعيين يوميًا كحد أقصى.',
  'admin.access.reset.methodLabel': 'كيف تم التحقق من هوية الشخص؟',
  'admin.access.reset.methodHint':
    'خارج المنصة: لا يوجد بريد إلكتروني، وهذه هي الحماية الحقيقية. تُسجَّل هذه المعلومة في سجل التدقيق.',
  'admin.access.reset.method.phone_call': 'مكالمة هاتفية (رقم معروف)',
  'admin.access.reset.method.video_call': 'مكالمة فيديو',
  'admin.access.reset.method.in_person': 'شخصيًا',
  'admin.access.reset.methodPlaceholder': 'اختر طريقة',
  'admin.access.reset.submit': 'إنشاء كلمة مرور مؤقتة',
  'admin.access.reset.superadminNote':
    'لا تُعاد كلمة مرور المشرف الأعلى عبر التطبيق أبدًا: إجراء الطوارئ بشخصين (راجع دليل التشغيل).',
  'admin.access.reset.shopNote': 'تُعاد كلمة مرور مالك المتجر من قائمة التجار.',
  'errors.superadminNotResettable':
    'لا تُعاد كلمة مرور المشرف الأعلى عبر التطبيق: استخدم إجراء الطوارئ.',
  'errors.accountDisabled': 'الحساب معطّل.',
  'errors.accountNotLocked': 'الحساب غير مقفل.',
  'errors.insufficientStock': 'المخزون غير كافٍ: هذا السحب سيجعل الكمية المتوفرة أقل من صفر.',
  'errors.storageUnavailable':
    'خدمة الصور غير متاحة مؤقتًا. الملف ليس هو المشكلة: أعد المحاولة بعد قليل.',

  // ---- shopper side: cart, checkout, coupon, order lookup ----
  'errors.cartNotFound': 'تعذّر العثور على سلتك أو انتهت صلاحيتها. عد إلى المتجر للبدء من جديد.',
  'errors.cartItemNotFound': 'هذا المنتج لم يعد في سلتك.',
  'errors.cartEmpty': 'سلتك فارغة.',
  'errors.productNotAvailable': 'هذا المنتج لم يعد متوفرًا.',
  'errors.stockExceeded': 'المخزون غير كافٍ لهذه الكمية.',
  'errors.variantRequired': 'اختر خيارًا (المقاس، اللون…) قبل إضافة هذا المنتج.',
  'errors.variantNotFound': 'هذا الخيار غير موجود لهذا المنتج.',
  'errors.couponNotFound': 'رمز التخفيض هذا غير موجود.',
  'errors.couponNotYetValid': 'رمز التخفيض هذا غير صالح بعد.',
  'errors.couponExpired': 'انتهت صلاحية رمز التخفيض هذا.',
  'errors.couponMinimumNotMet': 'طلبك لا يبلغ الحد الأدنى المطلوب لرمز التخفيض هذا.',
  'errors.couponUsageLimitReached': 'بلغ رمز التخفيض هذا الحد الأقصى للاستخدام.',
  'errors.orderNotFound': 'لا يوجد طلب يطابق هذا الرقم ورقم الهاتف.',

  // ---- account and security (lot B3) ----
  'account.nav': 'حسابي',
  'account.title': 'حسابي',
  'account.subtitle': 'عمليات دخولك وجلساتك وسجل الأمان لحسابك.',
  'account.changePassword': 'تغيير كلمة مروري',
  'account.lastSignIn.title': 'آخر تسجيل دخول',
  'account.lastSignIn.value': '{{date}}، من {{ip}}',
  'account.lastSignIn.valueNoIp': '{{date}}',
  'account.lastSignIn.none': 'هذه أول عملية دخول لك.',
  'account.lastSignIn.notice': 'آخر تسجيل دخول: {{date}}',
  'account.lastSignIn.dismiss': 'إغلاق',
  'account.lastSignIn.hint': 'لم تكن أنت؟ غيّر كلمة مرورك وأغلق الجلسات الأخرى.',
  'account.loadError': 'تعذّر تحميل السجل.',
  'account.sessions.title': 'جلساتي',
  'account.sessions.caption': 'الجلسات',
  'account.sessions.retention': 'يُحتفظ بعنوان IP والمتصفح لمدة 90 يومًا ثم يُمحيان.',
  'account.sessions.colOpened': 'فُتحت في',
  'account.sessions.colLastUsed': 'آخر نشاط',
  'account.sessions.colIp': 'عنوان IP',
  'account.sessions.colBrowser': 'المتصفح',
  'account.sessions.colState': 'الحالة',
  'account.sessions.current': 'هذه الجلسة',
  'account.sessions.active': 'نشطة',
  'account.sessions.ended': 'مغلقة',
  'account.sessions.erased': 'مُمحى (أكثر من 90 يومًا)',
  'account.sessions.empty': 'لا توجد جلسات.',
  'account.sessions.revokeOthers': 'تسجيل خروجي من كل مكان آخر',
  'account.sessions.revokeOthersHint':
    'يُغلق كل جلساتك الأخرى ويُبقي هذه. افعل ذلك إذا اعتقدت أن جهازًا آخر يصل إلى حسابك.',
  'account.sessions.revokedToast': 'تم إغلاق {{count}} جلسة أخرى.',
  'account.events.title': 'سجل الأمان',
  'account.events.caption': 'أحداث الأمان',
  'account.events.colWhen': 'متى',
  'account.events.colEvent': 'الحدث',
  'account.events.colIp': 'عنوان IP',
  'account.events.empty': 'لا توجد أحداث.',
  'account.event.auth.login_succeeded': 'تسجيل دخول ناجح',
  'account.event.auth.login_failed': 'محاولة دخول فاشلة',
  'account.event.auth.account_locked': 'قُفل الحساب (محاولات فاشلة كثيرة)',
  'account.event.auth.password_changed': 'تغيّرت كلمة المرور',
  'account.event.auth.password_change_failed': 'رُفض تغيير كلمة المرور',
  'account.event.auth.password_reset_completed': 'أُعيد تعيين كلمة المرور',
  'account.event.auth.sessions_revoked': 'أُغلقت الجلسات',
  'account.event.auth.reauth_failed': 'رُفض تأكيد كلمة المرور',
  'account.event.auth.temporary_password_expired': 'انتهت صلاحية كلمة المرور المؤقتة',
  'account.event.unknown': 'حدث أمني',
  'admin.nav.security': 'الأمان والتدقيق',
  'admin.security.title': 'الأمان والتدقيق',
  'admin.security.subtitle':
    'سجل التدقيق وعمليات الدخول والتنبيهات. للقراءة فقط: لا يمكن تعديل السجل ولا محوه، وكل اطلاع يُسجَّل فيه.',
  'admin.security.tab.label': 'أقسام الأمان والتدقيق',
  'admin.security.tab.audit': 'سجل التدقيق',
  'admin.security.tab.events': 'عمليات الدخول',
  'admin.security.tab.alerts': 'التنبيهات',
  'admin.security.reasonPlaceholder': 'مثال: المراجعة الشهرية للأمان',
  'admin.security.reasonRequired': 'اذكر سبب الاطلاع على السجل: الاطلاع نفسه مُدقَّق.',
  'admin.security.loadError': 'تعذّر تحميل البيانات.',
  'admin.security.audit.filterAction': 'الإجراء (بادئة)',
  'admin.security.audit.filterActionPlaceholder': 'مثال: superadmin. أو auth.login',
  'admin.security.audit.filterTarget': 'الهدف (المعرّف)',
  'admin.security.audit.filterActor': 'الفاعل (UUID)',
  'admin.security.audit.filterTenant': 'المؤسسة (UUID)',
  'admin.security.audit.filterFrom': 'من',
  'admin.security.audit.filterTo': 'إلى (غير شامل)',
  'admin.security.audit.apply': 'تطبيق',
  'admin.security.audit.caption': 'سجل التدقيق',
  'admin.security.audit.colWhen': 'متى',
  'admin.security.audit.colActor': 'الفاعل',
  'admin.security.audit.colAction': 'الإجراء',
  'admin.security.audit.colTarget': 'الهدف',
  'admin.security.audit.colIp': 'عنوان IP',
  'admin.security.audit.colDetails': 'التفاصيل',
  'admin.security.audit.anonymous': 'مجهول',
  'admin.security.audit.empty': 'لا توجد إدخالات لهذه المرشحات.',
  'admin.security.audit.loadMore': 'تحميل إدخالات أقدم',
  'admin.security.audit.export': 'تصدير بصيغة CSV',
  'admin.security.audit.exportHint':
    '5000 سطر كحد أقصى، مع المرشحات والسبب أعلاه. التصدير نفسه يُسجَّل في السجل.',
  'admin.security.audit.exported': 'تم تنزيل الملف.',
  'admin.security.audit.exportError': 'فشل التصدير.',
  'admin.security.events.hours': 'الفترة',
  'admin.security.events.hoursOption': '{{hours}} س',
  'admin.security.events.totals': 'الإجماليات',
  'admin.security.events.failedByIp': 'الإخفاقات حسب عنوان IP',
  'admin.security.events.failedByIdentifier': 'الإخفاقات حسب المعرّف (بصمة مقتطعة)',
  'admin.security.events.empty': 'لا شيء في هذه الفترة.',
  'admin.security.events.colEvent': 'الحدث',
  'admin.security.events.colCount': 'العدد',
  'admin.security.events.colKey': 'المفتاح',
  'admin.security.alerts.empty': 'لا توجد تنبيهات: لا شيء يتجاوز الحدود.',
  'admin.security.alerts.note':
    'تُحسب عند كل اطلاع من السجل والجلسات: لا شيء يُخزَّن أو يُرسل (لا يوجد بريد إلكتروني).',
  'admin.security.severity.critical': 'حرج',
  'admin.security.severity.warning': 'تحذير',
  'admin.security.severity.info': 'معلومة',
  'admin.security.alert.login_failures_spike': '{{count}} محاولة دخول فاشلة في الساعة الأخيرة',
  'admin.security.alert.login_failures_single_ip':
    '{{count}} محاولة دخول فاشلة من عنوان واحد في الساعة الأخيرة',
  'admin.security.alert.accounts_locked': 'قُفل {{count}} حساب خلال آخر 24 ساعة',
  'admin.security.alert.break_glass_executed': 'نُفّذ إجراء الطوارئ ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.last_superadmin_override':
    'استُخدم استثناء «آخر مشرف أعلى» ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.superadmin_promoted': 'ترقية مشرف أعلى ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.platform_role_revoked': 'سُحب دور منصة ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.platform_account_disabled': 'عُطّل حساب منصة ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.platform_admin_created': 'أُنشئ مسؤول ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.owner_password_reissued':
    'أُعيد تعيين كلمة مرور مالك ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.admin_password_reissued':
    'أُعيد تعيين كلمة مرور مسؤول ({{count}}) خلال آخر 24 ساعة',
  'admin.security.alert.promotion_pending': '{{count}} ترقية مشرف أعلى بانتظار التأكيد',
  'admin.security.alert.superadmin_new_address':
    '{{count}} تسجيل دخول لمشرف أعلى من عنوان لم يُرَ من قبل (24 ساعة)',
  'admin.security.alert.no_second_superadmin':
    'أقل من مشرفين أعلى نشطين: لا يمكن للتطبيق ترقية أحد',
  'admin.security.alert.unknown': 'تنبيه أمني',

  // ---- admin: nav ----
  'admin.nav.merchants': 'التجار',

  // ---- admin: auth ----
  'admin.auth.loginTitle': 'تسجيل الدخول',
  'admin.auth.loginSubtitle': 'الدخول إلى إدارة المنصة.',
  'admin.auth.loginButton': 'تسجيل الدخول',
  'admin.auth.passwordLabel': 'كلمة المرور',

  // ---- admin: shared reason field ----
  'admin.common.reasonLabel': 'السبب (10 أحرف كحد أدنى، إلزامي وخاضع للتدقيق)',
  'admin.common.reasonConsultationLabel': 'سبب الاطلاع (اختياري)',
  'admin.common.reasonRequiredTitle': 'السبب مطلوب',

  // ---- admin: merchants ----
  'admin.merchants.subtitle': 'قائمة انتظار الموافقة وإدارة حالة تجار المنصة.',
  'admin.merchants.reasonPlaceholder': 'مثال: معالجة قائمة انتظار الموافقة اليومية',
  'admin.merchants.filterStatusAria': 'تصفية حسب الحالة',
  'admin.merchants.reasonRequiredDescription':
    'أدخل سبب اطلاع (10 أحرف كحد أدنى) لتحميل القائمة — كل قراءة في هذا المجال خاضعة للتدقيق.',
  'admin.merchants.loadError': 'تعذر تحميل التجار',
  'admin.merchants.accessDeniedDescription':
    'حسابك لا يملك دور مسؤول المنصة، أو لم يتم تقديم التحقق بخطوتين.',
  'admin.merchants.emptyForFilter': 'لا يوجد تاجر لهذا الفلتر',
  'admin.merchants.paginationSummary': '{{from}}–{{to}} من أصل {{total}}',
  'admin.merchants.table.colLegalName': 'الاسم القانوني',
  'admin.merchants.table.colCreatedAt': 'تاريخ الإنشاء',
  'admin.merchants.action.approve': 'الموافقة',
  'admin.merchants.action.suspend': 'إيقاف',
  'admin.merchants.action.reactivate': 'إعادة التفعيل',
  'admin.merchants.updateModalTitle': 'تحديث حالة التاجر',
  'admin.merchants.updatedToast': 'تم تحديث حالة التاجر.',
  'admin.merchants.noEmailHint':
    'لا يُرسل أي بريد إلكتروني إلى التاجر بشأن هذا التغيير: أبلغه مباشرة عند الحاجة.',

  // ---- admin: platform roles ----
  'admin.roles.subtitle': 'الاطلاع على انتماءات حساب (مُدقَّق).',
  'admin.roles.lookupSectionTitle': 'الاطلاع على انتماءات مستخدم',
  'admin.roles.noMembershipsTitle': 'لا يوجد انتماء لهذا المستخدم',
  'admin.roles.membershipsTableCaption': 'الانتماءات',
  'admin.roles.table.colTenant': 'المتجر (tenant)',
  'admin.roles.userIdLabel': 'معرّف المستخدم (UUID)',
  'admin.roles.lookupButton': 'الاطلاع على الانتماءات',

  // ---- المشرف: تزويد مؤسسة ----
  'admin.nav.organisations': 'تاجر جديد',
  'admin.organisations.title': 'إنشاء حساب تاجر',
  'admin.organisations.subtitle':
    'ينشئ المتجر وحساب مالكه. تُنشأ كلمة مرور مؤقتة وتُعرض مرة واحدة فقط: تسلّمها للتاجر وعليه اختيار كلمة مرور جديدة عند أول تسجيل دخول. لا يُرسل أي بريد إلكتروني.',
  'admin.organisations.emailLabel': 'البريد الإلكتروني للمالك',
  'admin.organisations.legalNameLabel': 'الاسم القانوني',
  'admin.organisations.organisationActiveLabel': 'الموافقة على المتجر فورًا',
  'admin.organisations.organisationActiveHint':
    'إذا لم يُحدَّد، ينضم المتجر إلى قائمة الموافقة المعتادة.',
  'admin.organisations.reasonLabel': 'السبب (يُسجَّل في التدقيق)',
  'admin.common.confirmPasswordLabel': 'كلمة مرورك (للتأكيد)',
  'admin.common.confirmPasswordHint':
    'إجراء حساس: أدخل كلمة مرورك الخاصة مرة أخرى. لا تُحفظ ولا تُعرض.',
  'admin.common.confirmPasswordRequired': 'أدخل كلمة مرورك للتأكيد.',
  'admin.merchants.action.refuse': 'رفض',
  'admin.merchants.action.resetOwner': 'كلمة مرور المالك',
  'admin.merchants.statusRefused': 'مرفوض',
  'admin.nav.portfolios': 'المحافظ',
  'admin.merchants.scopeAdminInfo': 'ترى فقط تجار محفظتك: الذين أنشأتهم أو أسندهم إليك مشرف أعلى.',
  'admin.merchants.scopeSuperInfo':
    'ترى جميع تجار المنصة، المسندين وغير المسندين. يمكنك إسنادهم إلى مسؤول؛ وحده المسؤول المكلف يقبل المتجر أو يرفضه.',
  'admin.merchants.portfolioEmptyTitle': 'محفظتك فارغة',
  'admin.merchants.portfolioEmptyDescription':
    'لا تدير أي تاجر بعد. التجار الذين تنشئهم يظهرون هنا. المتاجر الحالية أو قيد الانتظار لا تظهر لك ما لم يسندها إليك مشرف أعلى.',
  'admin.merchants.portfolioEmptyAction': 'إنشاء تاجر',
  'admin.merchants.noUnassignedTitle': 'لا توجد متاجر غير مسندة',
  'admin.merchants.searchLabel': 'البحث عن تاجر',
  'admin.merchants.searchPlaceholder': 'الاسم التجاري (حرفان على الأقل)',
  'admin.merchants.scopeLabel': 'المحفظة',
  'admin.merchants.scopeAll': 'جميع التجار',
  'admin.merchants.scopeUnassigned': 'غير المسندة',
  'admin.merchants.table.colManager': 'يديره',
  'admin.merchants.unassigned': 'غير مسندة',
  'admin.merchants.unassignedHint': 'يجب إسنادها إلى مسؤول قبل أي مصادقة.',
  'admin.merchants.pendingForManager': 'يصادق عليها المسؤول المكلف.',
  'admin.merchants.selectRowAria': 'تحديد {{name}}',
  'admin.merchants.selectAllAria': 'تحديد جميع متاجر الصفحة',
  'admin.merchants.assignSelection': 'إسناد المحدد ({{count}})',
  'admin.merchants.action.assign': 'إسناد',
  'admin.merchants.action.reassign': 'إعادة إسناد',
  'admin.assign.title': 'الإسناد إلى مسؤول',
  'admin.assign.intro':
    'سيُسند {{count}} متجر(ات) إلى المسؤول المختار، وسيكون وحده من يراها ويصادق عليها. العملية مدوّنة في السجل.',
  'admin.assign.adminLabel': 'المسؤول المكلف',
  'admin.assign.adminPlaceholder': 'اختر مسؤولاً',
  'admin.assign.noAdmins': 'لا يوجد مسؤول نشط. أنشئ واحداً في «المسؤولون».',
  'admin.assign.confirm': 'إسناد',
  'admin.assign.success': 'تم الإسناد ({{count}}).',
  'admin.portfolios.title': 'المحافظ',
  'admin.portfolios.subtitle':
    'من يدير كم تاجراً. المشرف الأعلى يسند وينقل، ولا يصادق على متجر أبداً.',
  'admin.portfolios.reasonRequiredDescription':
    'اكتب سبب الاطلاع (10 أحرف على الأقل) لعرض المحافظ؛ كل قراءة مدوّنة.',
  'admin.portfolios.loadError': 'تعذّر تحميل المحافظ.',
  'admin.portfolios.colAdmin': 'المسؤول',
  'admin.portfolios.colTotal': 'التجار',
  'admin.portfolios.colPending': 'قيد الانتظار',
  'admin.portfolios.colActive': 'نشطون',
  'admin.portfolios.colSuspended': 'موقوفون',
  'admin.portfolios.colRefused': 'مرفوضون',
  'admin.portfolios.disabledBadge': 'حساب معطّل',
  'admin.portfolios.unassignedRow': 'متاجر غير مسندة',
  'admin.portfolios.unassignedRowHint':
    'يراها المشرف الأعلى وحده. لا أحد يستطيع المصادقة عليها قبل إسنادها.',
  'admin.portfolios.viewMerchants': 'عرض التجار',
  'admin.portfolios.assignUnassigned': 'إسناد…',
  'admin.portfolios.transfer': 'نقل المحفظة',
  'admin.portfolios.empty': 'لا يوجد مسؤولون.',
  'admin.transfer.title': 'نقل محفظة {{name}}',
  'admin.transfer.intro':
    'سينتقل {{count}} تاجر(اً) من هذا المسؤول إلى المسؤول المختار. لا يمكن تعطيله أو سحب دوره إلا بعد أن تفرغ محفظته.',
  'admin.transfer.targetLabel': 'المسؤول المستلم',
  'admin.transfer.confirm': 'نقل',
  'admin.transfer.success': 'تم نقل المحفظة ({{count}}).',
  'admin.organisations.managerLabel': 'المسؤول المكلف',
  'admin.organisations.managerHint':
    'المشرف الأعلى ليس مديراً: اختر المسؤول الذي سيدير هذا التاجر ويصادق عليه.',
  'admin.organisations.managerRequired': 'اختر المسؤول المكلف.',
  'admin.organisations.ownPortfolioNote':
    'سيُربط هذا التاجر بمحفظتك: أنت وحدك من يراه ويصادق عليه (ويمكن لمشرف أعلى إعادة إسناده).',
  'admin.handover.title': 'ورقة التسليم للتاجر',
  'admin.handover.intro':
    'تُسلَّم للتاجر. أما كلمة المرور المؤقتة فتُسلَّم على حدة: مباشرة أو عبر قناة تثق بها.',
  'admin.handover.dashboardLabel': 'عنوان فضاء التاجر',
  'admin.handover.dashboardMissing':
    'العنوان غير مضبوط: أبلغه للتاجر (الإعداد VITE_MERCHANT_DASHBOARD_URL).',
  'admin.handover.accountLabel': 'المعرّف (البريد الإلكتروني للحساب)',
  'admin.handover.expiresLabel': 'كلمة المرور المؤقتة صالحة حتى',
  'admin.handover.stepsTitle': 'ما على التاجر فعله',
  'admin.handover.step1': 'افتح العنوان أعلاه وسجّل الدخول ببريدك الإلكتروني وكلمة المرور المؤقتة.',
  'admin.handover.step2': 'اختر كلمة مرورك الخاصة (12 حرفاً على الأقل).',
  'admin.handover.step3': 'أكمل متجرك: سيراه الجمهور بمجرد أن توافق عليه المنصة.',
  'admin.handover.copy': 'نسخ التعليمات',
  'admin.handover.copied': 'تم نسخ التعليمات (دون كلمة المرور).',
  'admin.handover.copyFailed': 'فشل النسخ: دوّن التعليمات يدوياً.',
  'admin.handover.noPassword': 'لا تتضمن هذه الورقة كلمة المرور أبداً.',
  'admin.organisations.localeLabel': 'لغة الحساب',
  'admin.organisations.localeHint': 'لغة شاشات التاجر عند أول دخول له.',
  'admin.merchants.action.reissueUnused': 'كلمة مرور مؤقتة جديدة',
  'admin.merchants.reissueUnusedWarning':
    'ممكن ما دام المالك لم يسجّل الدخول قط. منذ أول دخول له، وحده المشرف الأعلى يستطيع إعادة تعيين كلمة مروره.',
  'admin.merchants.refuseModalTitle': 'رفض هذا المتجر',
  'admin.merchants.suspendModalTitle': 'تعليق هذا المتجر',
  'admin.merchants.refuseWarning':
    'الرفض نهائي: لا يمكن بعد ذلك الموافقة على المتجر وعلى صاحب الطلب أن يسجّل من جديد.',
  'admin.merchants.suspendWarning': 'تختفي واجهة المتجر العامة فورًا.',
  'admin.merchants.resetOwnerTitle': 'كلمة مرور جديدة للمالك',
  'admin.merchants.resetOwnerWarning':
    'يتم إنشاء كلمة مرور مؤقتة وتُعرض مرة واحدة فقط. تُغلق جلسات المالك وعليه اختيار كلمة مرور جديدة عند تسجيل الدخول التالي. لا يُرسل أي بريد إلكتروني: تسلّمه كلمة المرور بنفسك.',
  'admin.merchants.resetOwnerGenerate': 'إنشاء كلمة المرور',
  'admin.organisations.statusActive': 'تمت الموافقة على المتجر.',
  'admin.organisations.statusPending': 'المتجر في انتظار الموافقة.',
  'admin.organisations.createAnother': 'إنشاء حساب آخر',
  'errors.reauthRequired': 'أكّد كلمة مرورك للمتابعة.',
  'errors.reauthFailed': 'كلمة المرور غير صحيحة. بعد عدة أخطاء يُقفل الحساب لبضع دقائق.',
  'errors.emailUnavailable': 'لا يمكن استخدام هذا العنوان لحساب جديد.',
  'errors.accountBelongsToShop':
    'هذا الحساب ينتمي إلى متجر: لا يمكنه الحصول على دور على مستوى المنصة.',
  'errors.ownerAmbiguous': 'لهذا المتجر عدة مالكين: تتم إعادة التعيين عبر إجراء المشغّل.',
  'errors.ownerDisabled': 'حساب المالك معطّل.',
  'admin.organisations.submit': 'إنشاء الحساب',
  'admin.organisations.success': 'تم إنشاء الحساب.',

  // ---- admin: analytics ----
  'admin.analytics.title': 'تحليلات المنصة',
  'admin.analytics.subtitle': 'مؤشرات حقيقية، محسوبة عبر جميع متاجر المنصة.',
  'admin.analytics.reasonPlaceholder': 'مثال: مراجعة أسبوعية لأداء المنصة',
  'admin.analytics.loadError': 'تعذر تحميل التحليلات',
  'admin.analytics.gmv': 'GMV',
  'admin.analytics.ordersAllShops': 'الطلبات (جميع المتاجر)',
  'admin.analytics.activeMerchants': 'التجار النشطون',
  'admin.analytics.pendingMerchants': 'التجار قيد الانتظار',
  'admin.analytics.byStatusTitle': 'التجار حسب الحالة',
  'admin.analytics.noMerchants': 'لا يوجد تاجر مسجّل',

  // ---- merchant: clients ----
  'merchant.clients.subtitle':
    'أُعيد تكوينها من طلباتك الفعلية (لا يوجد بعد نقطة نهاية لقائمة العملاء) — كل عميل يقابل رقم هاتف.',
  'merchant.clients.truncatedNotice': 'للعلم',
  'merchant.clients.truncatedDescription':
    'هذه القائمة مبنية على آخر 2000 طلب لديك. قد يفوتك عميل أقدم بعد ذلك.',
  'merchant.clients.loadError': 'تعذر تحميل العملاء',
  'merchant.clients.emptyTitle': 'لا يوجد عملاء حتى الآن',
  'merchant.clients.emptyDescription': 'سيظهر عملاؤك هنا فور استلام أول طلب.',
  'merchant.clients.table.colClient': 'العميل',
  'merchant.clients.table.colLastActivity': 'آخر نشاط',
  'merchant.clients.table.colTotalSpent': 'إجمالي الإنفاق',
  'merchant.clients.viewDetail': 'عرض التفاصيل',
  'merchant.clients.orderCountLabel': 'طلب(ات)',
  'merchant.clients.orderHistoryTitle': 'سجل الطلبات',
  'merchant.clients.purchasedProductsTitle': 'المنتجات المشتراة',
  'merchant.clients.noNonCancelledProducts': 'لا يوجد منتج ضمن طلب غير ملغى.',
  'merchant.clients.couponsUsedTitle': 'القسائم المستخدمة',
  'merchant.clients.noCouponUsed': 'لم تُستخدم أي قسيمة',
  'merchant.clients.noCouponUsedDescription': 'لم يستخدم هذا العميل أي قسيمة من المتجر بعد.',
  'merchant.clients.couponOn': 'على',

  // ---- merchant: marketing ----
  'merchant.marketing.subtitlePrefix': 'الأداء الفعلي لقسائمك. لإنشاء أو تعديل قسيمة، توجّه إلى',
  'merchant.marketing.loadError': 'تعذر تحميل بيانات التسويق',
  'merchant.marketing.activeCoupons': 'القسائم النشطة',
  'merchant.marketing.activeCouponsHint': 'من أصل {{total}} قسيمة إجمالاً',
  'merchant.marketing.totalUsage': 'إجمالي الاستخدامات',
  'merchant.marketing.couponsCreated': 'القسائم المُنشأة',
  'merchant.marketing.performanceTitle': 'أداء العروض الترويجية',
  'merchant.marketing.emptyTitle': 'لا توجد قسيمة حتى الآن',
  'merchant.marketing.emptyDescription': 'أنشئ قسيمة من العروض الترويجية لرؤية أدائها هنا.',
  'merchant.marketing.table.colCode': 'الرمز',
  'merchant.marketing.table.colValue': 'القيمة',
  'merchant.marketing.table.colUsageRate': 'معدل الاستخدام',
  'merchant.marketing.usageUnlimited': '{{count}} (غير محدود)',
  'merchant.marketing.campaignsTitle': 'الحملات',
  'merchant.marketing.campaignsDescription':
    'ميزة غير متاحة بعد. لا توجد وحدة حملات (بريد إلكتروني، رسائل نصية) في المنصة حالياً — القسائم أعلاه فقط حقيقية وقابلة للاستخدام اليوم.',

  // ---- merchant: payments ----
  'merchant.payments.subtitle':
    'الدفع عند الاستلام فقط. هذا العرض محسوب من حالة طلباتك — لا توجد بيانات دفع منفصلة معروضة في هذه الشاشة بعد.',
  'merchant.payments.loadError': 'تعذر تحميل المدفوعات',
  'merchant.payments.truncatedDescription': 'تشمل هذه الأرقام آخر 2000 طلب لديك.',
  'merchant.payments.amountCollected': 'المبلغ المحصّل',
  'merchant.payments.amountCollectedHint': 'الطلبات ذات الدفع المؤكد',
  'merchant.payments.pendingKpi': 'قيد الانتظار',
  'merchant.payments.confirmedKpi': 'مؤكدة',
  'merchant.payments.failedKpi': 'فاشلة',
  'merchant.payments.filterAria': 'تصفية حسب حالة الدفع',
  'merchant.payments.emptyForStatus': 'لا يوجد طلب لهذه الحالة',
  'merchant.payments.table.colPayment': 'الدفع',
  'merchant.payments.bucket.pending': 'قيد الانتظار',
  'merchant.payments.bucket.confirmed': 'مؤكد',
  'merchant.payments.bucket.failed': 'فاشل',
  'merchant.payments.bucket.refunded': 'مسترد',
  'merchant.payments.bucket.cancelled': 'ملغى',

  // ---- merchant: delivery ----
  'merchant.delivery.subtitle': 'الطلبات بانتظار الشحن، مع سجل محاولاتها.',
  'merchant.delivery.loadError': 'تعذر تحميل الشحنات',
  'merchant.delivery.emptyTitle': 'لا توجد شحنة بانتظار',
  'merchant.delivery.emptyDescription':
    'ستظهر هنا الطلبات المُعلّمة «تم التغليف» إذا لم ينجح إرسالها بعد.',
  'merchant.delivery.tableCaption': 'الشحنات المعلّقة',
  'merchant.delivery.table.colShipment': 'الشحنة',
  'merchant.delivery.table.colAttempts': 'محاولات الإرسال',
  'merchant.delivery.table.colDetail': 'التفاصيل',
  'merchant.delivery.table.colLastAttempt': 'آخر محاولة',
  'merchant.delivery.noDetail': 'لا تفاصيل حالياً.',
  'merchant.delivery.status.pendingDispatch': 'بانتظار الشحن',
  'merchant.delivery.status.dispatchFailed': 'فشل الشحن',
  // ---- Expéditions (Jibex) ----
  'merchant.ship.title': 'الشحنات',
  'merchant.ship.subtitle': 'طرودك لدى Jibex: حالتها التي تُتابَع تلقائيًا، وما يمكنك فعله.',
  'merchant.ship.noAccount': 'لا يوجد حساب Jibex مرتبط: الطلبات «المُجهّزة» لا تُرسل.',
  'merchant.ship.noAccountLink': 'ربط Jibex',
  'merchant.ship.tab.in_progress': 'قيد التوصيل',
  'merchant.ship.tab.to_send': 'للإرسال',
  'merchant.ship.tab.failed': 'إرسال فاشل',
  'merchant.ship.tab.delivered': 'مُسلَّمة',
  'merchant.ship.tab.returns': 'المرتجعات والإلغاءات',
  'merchant.ship.tab.attention': 'تحتاج معالجة',
  'merchant.ship.tableCaption': 'الشحنات',
  'merchant.ship.col.order': 'الطلب',
  'merchant.ship.col.recipient': 'المستلم',
  'merchant.ship.col.status': 'الحالة',
  'merchant.ship.col.tracking': 'رقم التتبع',
  'merchant.ship.col.updated': 'آخر تحديث',
  'merchant.ship.col.open': 'التفاصيل',
  'merchant.ship.empty.title': 'لا توجد شحنات هنا',
  'merchant.ship.empty.description': 'الطلبات التي تنتقل إلى «مُجهّزة» تُرسل إلى Jibex وتظهر هنا.',
  'merchant.ship.status.pending_dispatch': 'للإرسال',
  'merchant.ship.status.dispatch_failed': 'فشل الإرسال',
  'merchant.ship.status.registered': 'مسجّل لدى Jibex',
  'merchant.ship.status.pickup_scheduled': 'الاستلام مُبرمج',
  'merchant.ship.status.in_transit': 'في الطريق',
  'merchant.ship.status.out_for_delivery': 'قيد التسليم',
  'merchant.ship.status.delivered': 'تم التسليم',
  'merchant.ship.status.return_in_progress': 'الإرجاع جارٍ',
  'merchant.ship.status.returned': 'مُرجَع',
  'merchant.ship.status.under_review': 'قيد التحقق',
  'merchant.ship.status.cancelled': 'ملغى',
  'merchant.ship.status.lost': 'مفقود',
  'merchant.ship.status.unknown': 'حالة غير معروفة',
  'merchant.ship.attention.under_review': 'Jibex يتحقق من هذا الطرد',
  'merchant.ship.attention.cancelled': 'ألغاه Jibex: القرار لك',
  'merchant.ship.attention.lost': 'طرد مفقود: اتصل بـ Jibex',
  'merchant.ship.attention.unknown_status': 'حالة Jibex غير معروفة',
  'merchant.ship.attention.unexpected_transition': 'تغيّر غير متوقع',
  'merchant.ship.attention.credentials': 'رُفضت بيانات اعتماد Jibex',
  'merchant.ship.attention.return': 'إرجاع للمتابعة',
  'merchant.ship.attention.cancellation_refused': 'رفض Jibex الإلغاء',
  'merchant.ship.source.carrier_poll': 'تتبع Jibex',
  'merchant.ship.source.carrier_notification': 'إشعار Jibex',
  'merchant.ship.source.create_response': 'إنشاء الطرد',
  'merchant.ship.source.system': 'ABOUEB',
  'merchant.ship.source.operator': 'إجراؤك',
  'merchant.ship.detail.title': 'الشحنة',
  'merchant.ship.detail.order': 'الطلب',
  'merchant.ship.detail.recipient': 'المستلم',
  'merchant.ship.detail.tracking': 'رقم التتبع',
  'merchant.ship.detail.copy': 'نسخ',
  'merchant.ship.detail.copied': 'تم النسخ',
  'merchant.ship.detail.reference': 'المرجع لدى Jibex',
  'merchant.ship.detail.carrierStatus': 'حالة Jibex',
  'merchant.ship.detail.reason': 'السبب الذي قدّمه Jibex',
  'merchant.ship.detail.cancellation': 'الإلغاء',
  'merchant.ship.detail.cancellationRequested': 'مطلوب، في انتظار قرار Jibex',
  'merchant.ship.detail.cancellationRefused': 'رفضه Jibex',
  'merchant.ship.detail.lastError': 'سبب فشل الإرسال',
  'merchant.ship.detail.nextPoll': 'التحقق القادم',
  'merchant.ship.detail.timeline': 'السجل',
  'merchant.ship.detail.noEvents': 'لا يوجد حدث بعد.',
  'merchant.ship.detail.amounts': 'المبالغ',
  'merchant.ship.detail.amountsNotice':
    'كما أرسلها Jibex. لم يؤكد Jibex بعد معناها الدقيق (خصم الرسوم أم لا): لا يستعملها ABOUEB في أي حساب، ويبقى الطلب والفاتورة دون تغيير.',
  'merchant.ship.detail.codSent': 'المبلغ المُرسل للتحصيل',
  'merchant.ship.detail.journal': 'سجل الاتصالات بـ Jibex',
  'merchant.ship.action.open': 'فتح',
  'merchant.ship.action.refresh': 'تحديث',
  'merchant.ship.action.retry': 'إعادة محاولة الإرسال',
  'merchant.ship.action.cancel': 'إلغاء الطرد',
  'merchant.ship.action.resend': 'إعادة إرسال الطلب',
  'merchant.ship.done.refresh': 'تم تحديث الحالة.',
  'merchant.ship.auto.note': 'يتم تحديث الحالة تلقائيًا من Jibex كل 60 ثانية.',
  'merchant.ship.auto.last': 'آخر تحديث تلقائي: {{time}} (كل 60 ثانية).',
  'merchant.sendJibex.title': 'التأكيد والإرسال إلى Jibex',
  'merchant.sendJibex.action': 'إرسال إلى Jibex',
  'merchant.sendJibex.orderLine': 'الطلب #{{number}} · {{total}}',
  'merchant.sendJibex.recipient': 'المرسل إليه',
  'merchant.sendJibex.parcel': 'معلومات الطرد',
  'merchant.sendJibex.phone': 'الهاتف',
  'merchant.sendJibex.phone2': 'الهاتف 2',
  'merchant.sendJibex.name': 'الاسم',
  'merchant.sendJibex.address': 'العنوان',
  'merchant.sendJibex.addressPlaceholder': 'الشارع، الحي، الرقم…',
  'merchant.sendJibex.governorate': 'الولاية',
  'merchant.sendJibex.delegation': 'المعتمدية',
  'merchant.sendJibex.delegationPlaceholder': 'مثال: باب بحر',
  'merchant.sendJibex.chooseGovernorateFirst': 'اختر الولاية أولاً',
  'merchant.sendJibex.delegationMismatch': 'اختر معتمدية من هذه الولاية.',
  'merchant.sendJibex.typedDelegation': 'المُدخل في الطلب: « {{value}} ». اختر المعتمدية المطابقة.',
  'merchant.sendJibex.comment': 'ملاحظة',
  'merchant.sendJibex.commentHint': 'يُرسل إلى Jibex مع الطرد (مع الهاتف 2).',
  'merchant.sendJibex.price': 'السعر (د.ت)',
  'merchant.sendJibex.priceHint': 'مبلغ الطلب، يُحصَّل عند التسليم.',
  'merchant.sendJibex.pieces': 'عدد القطع',
  'merchant.sendJibex.designation': 'الوصف',
  'merchant.sendJibex.designationHint': 'منتجات الطلب، تُرسل إلى Jibex.',
  'merchant.sendJibex.weight': 'الوزن (كغ)',
  'merchant.sendJibex.exchange': 'استبدال',
  'merchant.sendJibex.type': 'النوع',
  'merchant.sendJibex.typeFIX': 'FIX - ثابت',
  'merchant.sendJibex.fragile': 'قابل للكسر',
  'merchant.sendJibex.opening': 'السماح بالفتح',
  'merchant.sendJibex.yes': 'نعم',
  'merchant.sendJibex.no': 'لا',
  'merchant.sendJibex.select': 'اختر…',
  'merchant.sendJibex.required': 'حقل إلزامي.',
  'merchant.sendJibex.missingTitle': 'تنقص معلومات لإنشاء الطرد: أكمل الحقول المشار إليها.',
  'merchant.sendJibex.missingField': 'يجب إكماله',
  'merchant.sendJibex.phoneInvalid': '8 أرقام، مع أو بدون +216.',
  'merchant.sendJibex.weightInvalid': 'وزن أكبر من 0 كغ.',
  'merchant.sendJibex.piecesInvalid': 'عدد صحيح، 1 على الأقل.',
  'merchant.sendJibex.submit': 'إنشاء وإرسال',
  'merchant.sendJibex.sending': 'جارٍ الإرسال إلى Jibex…',
  'merchant.sendJibex.success': 'تم إنشاء الطرد لدى Jibex: {{tracking}}.',
  'merchant.sendJibex.failed':
    'لم ينشئ Jibex الطرد: {{reason}} صحّح ثم أعد الإرسال — بياناتك محفوظة.',
  'merchant.sendJibex.stillSending': 'يستمر الإرسال في الخلفية: تابعه في التوصيل.',
  'merchant.sendJibex.alreadySent': 'هذا الطلب موجود بالفعل لدى Jibex ({{tracking}}).',
  'merchant.sendJibex.inProgress': 'إرسال هذا الطلب جارٍ بالفعل.',
  'merchant.sendJibex.notSendable': 'لا يمكن إرسال هذا الطلب في حالته الحالية.',
  'merchant.sendJibex.noCarrier': 'لا يوجد حساب Jibex متصل: سيُعلَّم الطلب «جاهز» فقط دون إرسال.',
  'merchant.sendJibex.markPacked': 'تعليم كجاهز',
  'merchant.sendJibex.openDelivery': 'عرض في التوصيل',
  'merchant.sendJibex.phone2Invalid': 'الهاتف 2: 8 أرقام، مع أو بدون +216.',
  'merchant.ship.done.retry': 'أُعيد الإرسال: ستتحدّث الحالة بعد لحظات.',
  'merchant.ship.done.cancel': 'أُلغي الطرد لدى Jibex. عاد الطلب إلى «مُجهّز».',
  'merchant.ship.done.resend': 'أُعيد إرسال الطلب: سيُنشأ طرد جديد.',
  'merchant.ship.cancel.title': 'إلغاء هذا الطرد لدى Jibex؟',
  'merchant.ship.cancel.body': 'ممكن فقط ما دام Jibex لم يستلم الطرد بعد.',
  'merchant.ship.cancel.effect':
    'سيعود الطلب إلى «مُجهّز»: يمكنك تصحيحه ثم إعادة إرساله. لا يُعاد إرساله تلقائيًا.',
  'merchant.ship.cancel.keep': 'رجوع',
  'merchant.ship.cancel.confirm': 'إلغاء الطرد',
  'merchant.ship.city.label': 'المدينة المُرسلة إلى Jibex',
  'merchant.ship.city.hint':
    'الصيغة «الولاية، المعتمدية» (مثال: Tunis, Bab Bhar). لا يتغيّر عنوان الطلب.',
  'merchant.ship.city.save': 'حفظ المدينة',
  'merchant.ship.city.reset': 'العودة إلى عنوان الطلب',
  'merchant.ship.city.saved': 'حُفظت المدينة. أعد محاولة الإرسال لاستعمالها.',
  'merchant.ship.lastError.INVALID_PHONE': 'هاتف العميل ليس رقمًا تونسيًا من 8 أرقام.',
  'merchant.ship.lastError.INVALID_CITY': 'المدينة غير مقبولة: صحّحها أدناه ثم أعد المحاولة.',
  'merchant.ship.lastError.INVALID_ADDRESS': 'العنوان فارغ أو طويل جدًا.',
  'merchant.ship.lastError.INVALID_NAME': 'اسم المستلم فارغ أو طويل جدًا.',
  'merchant.ship.lastError.INVALID_REQUEST':
    'رفض Jibex بيانات الطرد (غالبًا المدينة): تحقّق منها وصحّح المدينة عند الحاجة ثم أعد المحاولة.',
  'merchant.ship.lastError.credentials':
    'يرفض Jibex بيانات اعتماد الحساب: تحقّق منها في «حساب Jibex».',
  'merchant.ship.lastError.FORBIDDEN':
    'مفتاح Jibex الخاص بك لا يملك الصلاحية اللازمة: اطلبها من Jibex.',
  'merchant.ship.lastError.ECOMMERCE_STORE_NOT_FOUND': 'معرّف متجر Jibex لا ينتمي إلى حسابك.',
  'merchant.ship.lastError.CARRIER_NOT_CONFIGURED': 'المنصة غير مرتبطة بـ Jibex بعد.',
  'merchant.ship.lastError.other': 'رفض Jibex الإرسال (الرمز {{code}}).',
  'merchant.ship.error.notFound': 'الشحنة غير موجودة.',
  'merchant.ship.error.notRetryable':
    'لا يمكن إعادة هذا الإرسال (لم يفشل، أو لم يعد الطلب «مُجهّزًا»).',
  'merchant.ship.error.notAtCarrier': 'هذا الطرد ليس لدى Jibex بعد.',
  'merchant.ship.error.notCancellable': 'لم يعد بالإمكان إلغاء هذا الطرد: استلمه Jibex بالفعل.',
  'merchant.ship.error.notResendable':
    'لا يمكن إعادة إرسال هذا الطلب (الطرد غير ملغى، أو لم يعد الطلب «مُجهّزًا»).',
  'merchant.ship.error.alreadyAtCarrier': 'الطرد لدى Jibex بالفعل: لم يعد بالإمكان تغيير المدينة.',
  'merchant.ship.error.invalidCity': 'مدينة غير صالحة: من 1 إلى 120 حرفًا.',
  'merchant.ship.error.accountUnavailable': 'لا يوجد حساب Jibex صالح: اربطه في «حساب Jibex».',
  'merchant.ship.error.credentialsRefused': 'يرفض Jibex بيانات اعتماد حسابك.',
  'merchant.ship.error.scopeMissing': 'مفتاح Jibex لا يملك صلاحية هذا الإجراء: اطلبها من Jibex.',
  'merchant.ship.error.carrierRateLimited': 'يطلب Jibex الانتظار قليلًا قبل إعادة المحاولة.',
  'merchant.ship.error.carrierUnavailable': 'لا يستجيب Jibex حاليًا. أعد المحاولة بعد بضع دقائق.',
  'merchant.ship.error.carrierRefused': 'رفض Jibex هذا الطلب.',
  'merchant.delivery.notifications.title': 'إشعارات Jibex',
  'merchant.delivery.notifications.description':
    'يُعلم Jibex منصة ABOUEB بكل تغيير في حالة الطرد: تتحدّث طلباتك فورًا. بدون إشعارات، يتحقق ABOUEB لدى Jibex بانتظام.',
  'merchant.delivery.notifications.unavailable':
    'لا يملك هذا الخادم عنوانًا عامًا لاستقبال الإشعارات. تتم المتابعة بالتحقق المنتظم.',
  'merchant.delivery.notifications.needAccount': 'اربط حساب Jibex أولًا.',
  'merchant.delivery.notifications.notConfigured': 'غير مفعّلة بعد.',
  'merchant.delivery.notifications.configured': 'مفعّلة. العنوان الذي يُعطى لـ Jibex:',
  'merchant.delivery.notifications.generate': 'إنشاء العنوان والسر',
  'merchant.delivery.notifications.rotate': 'تغيير السر',
  'merchant.delivery.notifications.rotateConfirm':
    'سيتوقف السر القديم فورًا: يجب أن يتلقّى Jibex السر الجديد.',
  'merchant.delivery.notifications.disable': 'إيقاف',
  'merchant.delivery.notifications.revealTitle': 'العنوان وسرّ التوقيع',
  'merchant.delivery.notifications.secretOnce': 'يُعرض السر مرة واحدة فقط. انسخه الآن.',
  'merchant.delivery.notifications.steps':
    'أرسل هذا العنوان وهذا السر إلى شركة التوصيل (Jibex) لتسجّلهما في ملف المرسل الخاص بك.',
  'merchant.delivery.notifications.urlLabel': 'عنوان الإشعارات',
  'merchant.delivery.notifications.secretLabel': 'سرّ التوقيع',
  'merchant.delivery.notifications.done': 'لقد نسخته',
  'merchant.delivery.notifications.errorUnavailable': 'لا يمكن تفعيل الإشعارات على هذا الخادم.',

  // ---- auth pages (Lot E3) ----
  'auth.email.label': 'البريد الإلكتروني',
  'auth.password.label': 'كلمة المرور',
  'auth.confirmPassword.label': 'تأكيد كلمة المرور',
  'auth.password.hint': '8 أحرف على الأقل.',
  'auth.validation.email': 'يبدو أن عنوان البريد الإلكتروني غير صحيح.',
  'auth.validation.passwordShort': 'يجب ألا تقل كلمة المرور عن 8 أحرف.',
  'auth.validation.chosenPasswordShort': 'يجب أن تتكون كلمة المرور من 12 حرفًا على الأقل.',
  'auth.validation.passwordsMismatch': 'كلمتا المرور غير متطابقتين.',
  // The ABOUEB brand panel of the merchant sign-in screens (features/auth/components/AuthLayout).
  'merchant.authBrand.space': 'فضاء التاجر',
  'merchant.authBrand.headline': 'افتح أبواب',
  'merchant.authBrand.headlineAccent': 'تجارتك الإلكترونية.',
  'merchant.authBrand.lead':
    'أدِر منتجاتك وطلباتك وعمليات التوصيل، وتابع كل شيء في الوقت الفعلي، أينما كنت.',
  'auth.login.title': 'تسجيل الدخول',
  'auth.login.subtitle': 'مرحباً بعودتك إلى فضائك التجاري.',
  'auth.login.submit': 'دخول',
  'auth.login.forgot': 'هل نسيت كلمة المرور؟',
  'auth.login.platformAccountNotice':
    'هل سلّمتك المنصة كلمة مرور مؤقتة؟ أدخلها هنا: ستُدعى إلى اختيار كلمة مرور جديدة. وهي صالحة لمدة 72 ساعة.',
  'auth.login.invalid':
    'البريد الإلكتروني أو كلمة المرور غير صحيحة. إذا حاولت عدة مرات فانتظر قليلاً ثم أعد المحاولة. كلمة المرور المؤقتة ضائعة أو منتهية (72 ساعة)؟ اتصل بدعم المنصة.',
  'auth.forgot.title': 'نسيت كلمة المرور',
  'auth.forgot.subtitle': 'أدخل عنوانك: سنرسل لك رابطاً لاختيار كلمة مرور جديدة.',
  'auth.forgot.submit': 'إرسال الرابط',
  'auth.forgot.back': 'العودة إلى تسجيل الدخول',
  'auth.forgot.sentTitle': 'تحقق من بريدك الإلكتروني',
  'auth.forgot.sentSubtitle': 'إذا كان هناك حساب بهذا العنوان فقد أُرسل إليه رابط للتو.',
  'auth.forgot.sentHint': 'تنتهي صلاحية الرابط بعد وقت قصير. تذكّر فحص البريد غير المرغوب فيه.',
  'auth.reset.title': 'كلمة مرور جديدة',
  'auth.reset.subtitle': 'اختر كلمة مرور لا تستخدمها في أي مكان آخر.',
  'auth.reset.newPassword': 'كلمة المرور الجديدة',
  'auth.reset.submit': 'حفظ كلمة المرور',
  'auth.reset.invalidTitle': 'رابط غير مكتمل',
  'auth.reset.invalidSubtitle': 'يبدو أن هذا الرابط قُطع في الطريق.',
  'auth.reset.invalidHint': 'تأكد من نسخ الرابط كاملاً أو اطلب رابطاً جديداً.',
  'auth.reset.requestNew': 'طلب رابط جديد',
  'auth.reset.failed':
    'لم يعد هذا الرابط صالحاً: انتهت صلاحيته أو سبق استخدامه. اطلب رابطاً جديداً.',
  'auth.reset.doneTitle': 'تم تحديث كلمة المرور',
  'auth.reset.doneSubtitle': 'يمكنك تسجيل الدخول بكلمة المرور الجديدة.',
  'auth.reset.doneHint': 'حفاظاً على أمانك، تم تسجيل خروجك من الجلسات الأخرى.',
  'auth.reset.signIn': 'تسجيل الدخول',
  'auth.changePassword.title': 'اختر كلمة مرورك',
  'auth.changePassword.subtitle':
    'كلمة المرور المؤقتة التي سُلّمت إليك مخصصة لهذه الخطوة فقط. اختر كلمة مرور لا يعرفها غيرك.',
  'auth.changePassword.currentLabel': 'كلمة المرور المؤقتة',
  'auth.changePassword.signOut': 'تسجيل الخروج',
  'auth.wrongSpace.merchant.title': 'حساب غير مرتبط بمتجر',
  'auth.wrongSpace.merchant.subtitle': 'هذه المساحة مخصصة للتجار.',
  'auth.wrongSpace.merchant.body':
    'هذا الحساب غير مرتبط بأي متجر. إذا كان حساب مشرف على المنصة، فسجّل الدخول إلى مساحة الإدارة. وإلا فاتصل بالمنصة.',
  'auth.wrongSpace.admin.title': 'حساب بدون دور إداري',
  'auth.wrongSpace.admin.subtitle': 'هذه المساحة مخصصة لمشرفي المنصة.',
  'auth.wrongSpace.admin.body':
    'هذا الحساب ليس له أي دور إداري. إذا كان حساب تاجر، فسجّل الدخول إلى مساحة التاجر.',
  'auth.login.noAccountFooter': 'لا حساب لديك بعد؟ تنشئ المنصة حسابات التجار: اتصل بمسؤولك.',
  'auth.changePassword.welcomeTitle': 'مرحباً — اختر كلمة مرورك',
  'auth.changePassword.welcomeSubtitle':
    'أنشأت المنصة حساب التاجر الخاص بك. كلمة المرور المؤقتة التي سُلّمت إليك لهذه الخطوة فقط: اختر كلمة مرور لا يعرفها غيرك.',
  'auth.changePassword.rule': '12 حرفاً على الأقل، ومختلفة عن كلمة المرور المؤقتة.',
  'auth.changePassword.step1': 'تسجيل الدخول',
  'auth.changePassword.step2': 'اختيار كلمة مروري',
  'auth.changePassword.step3': 'الدخول إلى فضائي',
  'auth.changePassword.steps': 'الخطوات',
  'auth.changePassword.provisionalWrong':
    'كلمة المرور المؤقتة غير صحيحة. أدخلها تماماً كما سُلّمت إليك. بعد عدة محاولات يُقفل الحساب مؤقتاً.',
  'auth.forgot.noEmailSubtitle': 'لا ترسل هذه المنصة رسائل بريد إلكتروني بعد.',
  'auth.forgot.noEmailBody':
    'لاستعادة الوصول إلى حسابك، اتصل بدعم المنصة: ستُسلَّم إليك كلمة مرور مؤقتة جديدة مباشرة.',
  'merchant.welcome.title': 'مرحباً.',
  'merchant.welcome.subtitle': 'أنشأت المنصة حسابك.',
  'merchant.welcome.passwordSaved': 'تم حفظ كلمة المرور.',
  'merchant.welcome.statusPending':
    'متجرك بانتظار الموافقة: لن يراه الجمهور حتى توافق عليه المنصة. يمكنك في هذه الأثناء تجهيزه.',
  'merchant.welcome.statusActive':
    'تمت الموافقة على متجرك. أكمله ليكون جاهزاً لاستقبال أول عملائه.',
  'merchant.welcome.cta': 'إكمال متجري',
  'merchant.welcome.dismiss': 'إغلاق',

  // ---- merchant: Jibex account (Lot L5a) ----
  'merchant.delivery.account.title': 'حساب جيبكس',
  'merchant.delivery.account.subtitle':
    'اربط حسابك الخاص في جيبكس: تُرسل شحناتك بمعرّفاتك أنت، وليس بمعرّفات تاجر آخر.',
  'merchant.delivery.account.back': 'العودة إلى الشحنات',
  'merchant.delivery.account.linkFromDelivery': 'حساب جيبكس',
  'merchant.delivery.account.ownerOnly': 'يمكن لمالك المتجر وحده إدارة حساب جيبكس.',
  'merchant.delivery.account.loadError': 'تعذّر تحميل حساب جيبكس',
  'merchant.delivery.account.platformNotReady':
    'الاتصال بجيبكس غير مفعّل بعد على المنصة. يمكنك حفظ معرّفاتك، وسيتاح اختبار الاتصال بعد التفعيل.',
  'merchant.delivery.account.state.not_configured': 'غير مُعدّ',
  'merchant.delivery.account.state.untested': 'لم يُختبر بعد',
  'merchant.delivery.account.state.connected': 'متصل',
  'merchant.delivery.account.state.invalid': 'معرّفات غير صالحة',
  'merchant.delivery.account.state.misconfigured': 'يحتاج إلى تصحيح',
  'merchant.delivery.account.state.network_error': 'خطأ في الشبكة',
  'merchant.delivery.account.stateText.not_configured':
    'لا يوجد حساب جيبكس محفوظ. أدخل معرّف العميل والسر أدناه.',
  'merchant.delivery.account.stateText.untested':
    'تم حفظ المعرّفات لكن لم يتم التحقق منها بعد. شغّل اختبار الاتصال.',
  'merchant.delivery.account.stateText.connected': 'قبلت جيبكس معرّفاتك.',
  'merchant.delivery.account.stateText.invalid':
    'رفضت جيبكس معرّفاتك. تحقق من معرّف العميل والسر ثم احفظهما من جديد.',
  'merchant.delivery.account.stateText.misconfigured':
    'قبلت جيبكس معرّفاتك لكن الحساب غير قابل للاستخدام كما هو. تحقق من معرّف المتجر أو تواصل مع جيبكس.',
  'merchant.delivery.account.stateText.network_error':
    'تعذّر إكمال آخر تحقق. لم تُعتبر معرّفاتك غير صالحة: أعد المحاولة بعد قليل.',
  'merchant.delivery.account.problem.invalidClient': 'رفضت جيبكس معرّف العميل أو السر.',
  'merchant.delivery.account.problem.clientInactive': 'هذا الحساب معطّل لدى جيبكس.',
  'merchant.delivery.account.problem.forbidden': 'رفضت جيبكس الوصول لهذا الحساب.',
  'merchant.delivery.account.problem.storeNotFound':
    'لم يتم العثور على متجر جيبكس المحدد. تحقق من معرّف المتجر.',
  'merchant.delivery.account.problem.rateLimited':
    'تحدّ جيبكس من الطلبات مؤقتاً. أعد المحاولة بعد قليل.',
  'merchant.delivery.account.problem.timeout': 'لم تردّ جيبكس في الوقت المناسب.',
  'merchant.delivery.account.problem.unreachable': 'تعذّر الوصول إلى جيبكس أو أن ردّها غير متوقع.',
  'merchant.delivery.account.problem.notActivated': 'الاتصال بجيبكس غير مفعّل على المنصة.',
  'merchant.delivery.account.problem.secretUnreadable': 'تعذّرت قراءة السر المحفوظ. أدخله من جديد.',
  'merchant.delivery.account.problem.unknown': 'خطأ غير متوقع (الرمز {{code}}).',
  'merchant.delivery.account.form.title': 'معرّفات جيبكس',
  'merchant.delivery.account.form.clientIdLabel': 'معرّف العميل',
  'merchant.delivery.account.form.clientIdHint': 'تقدّمه جيبكس. هذا ليس سراً.',
  'merchant.delivery.account.form.secretLabel': 'سر جيبكس',
  'merchant.delivery.account.form.secretHintNew':
    'الصق السر الذي قدّمته جيبكس. يُشفَّر فوراً ولن يُعرض مرة أخرى.',
  'merchant.delivery.account.form.secretHintKeep':
    'هناك سر محفوظ. اترك الحقل فارغاً للإبقاء عليه أو أدخل سراً جديداً لاستبداله.',
  'merchant.delivery.account.form.secretStored': 'السر محفوظ (مشفّر)',
  'merchant.delivery.account.form.storeIdLabel': 'معرّف متجر جيبكس',
  'merchant.delivery.account.form.storeIdHint': 'اختياري. لا تملأه إلا إذا أعطتك جيبكس معرّف متجر.',
  'merchant.delivery.account.form.save': 'حفظ',
  'merchant.delivery.account.form.saved': 'تم حفظ المعرّفات. شغّل اختبار الاتصال.',
  'merchant.delivery.account.form.clientIdRequired': 'معرّف العميل مطلوب.',
  'merchant.delivery.account.form.clientIdTooLong': 'معرّف العميل طويل جداً (200 حرفاً كحد أقصى).',
  'merchant.delivery.account.form.secretRequired': 'السر مطلوب لحفظ معرّف جديد.',
  'merchant.delivery.account.form.secretTooLong': 'السر طويل جداً (512 حرفاً كحد أقصى).',
  'merchant.delivery.account.form.charactersNotAllowed':
    'تُقبل الأحرف المطبوعة القياسية فقط (بدون أسطر جديدة أو أحرف مشكّلة).',
  'merchant.delivery.account.form.storeIdInvalid': 'يجب أن يكون معرّف المتجر عدداً صحيحاً موجباً.',
  'merchant.delivery.account.form.unsavedHint': 'احفظ تعديلاتك قبل اختبار الاتصال.',
  'merchant.delivery.account.security.title': 'حماية السر',
  'merchant.delivery.account.security.body':
    'يُشفَّر السر فور إدخاله. لا يُعرض أبداً ولا يُعاد إرساله من التطبيق ولا يُصدَّر. لتغييره أدخل سراً جديداً.',
  'merchant.delivery.account.status.title': 'حالة الحساب',
  'merchant.delivery.account.status.lastCheck': 'آخر تحقق: {{date}}',
  'merchant.delivery.account.status.neverChecked': 'لم يُتحقق منه قط',
  'merchant.delivery.account.status.clientId': 'معرّف العميل: {{clientId}}',
  'merchant.delivery.account.test.button': 'اختبار الاتصال',
  'merchant.delivery.account.test.running': 'جارٍ الاختبار…',
  'merchant.delivery.account.test.done': 'انتهى الاختبار: {{result}}',
  'merchant.delivery.account.test.tooMany': 'اختبارات كثيرة جداً. أعد المحاولة بعد {{seconds}} ث.',
  'merchant.delivery.account.test.unavailable': 'الخدمة غير متاحة مؤقتاً. أعد المحاولة بعد قليل.',
  'merchant.delivery.account.test.saveFirst': 'احفظ معرّفاتك أولاً.',
  'merchant.delivery.account.history.title': 'سجل التحقق',
  'merchant.delivery.account.history.empty': 'لا توجد عمليات تحقق بعد.',
  'merchant.delivery.account.history.loadError': 'تعذّر تحميل السجل',
  'merchant.delivery.account.history.caption': 'سجل التحقق من حساب جيبكس',
  'merchant.delivery.account.history.colDate': 'التاريخ',
  'merchant.delivery.account.history.colResult': 'النتيجة',
  'merchant.delivery.account.history.colDetail': 'التفاصيل',
  'merchant.delivery.account.history.colClient': 'معرّف العميل',
  'merchant.delivery.account.history.colDuration': 'المدة',
  'merchant.delivery.account.history.durationMs': '{{ms}} م.ث',
  'merchant.delivery.account.outcome.credentials_saved': 'حُفظت المعرّفات',
  'merchant.delivery.account.outcome.connected': 'متصل',
  'merchant.delivery.account.outcome.invalid_credentials': 'رُفضت المعرّفات',
  'merchant.delivery.account.outcome.misconfigured': 'يحتاج إلى تصحيح',
  'merchant.delivery.account.outcome.network_error': 'خطأ في الشبكة',
  'merchant.delivery.account.outcome.rate_limited': 'حدّ الطلبات',
  'merchant.delivery.account.outcome.unavailable': 'الخدمة غير مفعّلة',
  'merchant.delivery.account.outcome.disconnected': 'تم فصل الحساب',

  // ---- merchant: invoices ----
  'merchant.invoices.subtitle':
    'تُنشأ فاتورة تلقائياً لكل طلب مؤكد. تبقى الحقول الضريبية (ضريبة القيمة المضافة، الامتثال القانوني) معطّلة بانتظار التحقق منها.',
  'merchant.invoices.loadError': 'تعذر تحميل الفواتير',
  'merchant.invoices.emptyTitle': 'لا توجد فاتورة حتى الآن',
  'merchant.invoices.emptyDescription': 'ستظهر الفواتير هنا فور تأكيد أول طلب.',
  'merchant.invoices.downloadFailed': 'تعذر التنزيل.',
  'merchant.invoices.table.colInvoice': 'الفاتورة',
  'merchant.invoices.table.colAmount': 'المبلغ',
  'merchant.invoices.fiscal': 'ضريبية',
  'merchant.invoices.nonFiscal': 'غير ضريبية',
  'merchant.invoices.downloadPdf': 'تنزيل PDF',
  'merchant.invoices.invoiceNumberPrefix': 'رقم',

  // ---- merchant: staff ----
  'merchant.staff.inviteSuccess':
    'تم إرسال الدعوة إذا كان هناك حساب بهذا العنوان. تحقق من القائمة أدناه.',
  'merchant.staff.ownerOnlyTitle': 'الوصول مقتصر على المالك',
  'merchant.staff.ownerOnlyDescription': 'مالك المتجر وحده يمكنه إدارة الفريق.',
  'merchant.staff.subtitle': 'ادعُ أعضاء ABOUEB الحاليين وأدر صلاحياتهم.',
  'merchant.staff.inviteMember': 'دعوة عضو',
  'merchant.staff.loadError': 'تعذر تحميل الفريق',
  'merchant.staff.emptyTitle': 'لا يوجد أعضاء حتى الآن',
  'merchant.staff.inviteAction': 'دعوة',
  'merchant.staff.createAccount': 'إنشاء حساب',
  'merchant.staff.createAccountTitle': 'إنشاء حساب لعضو في الفريق',
  'merchant.staff.createAccountIntro':
    'تُنشأ كلمة مرور مؤقتة لهذا العضو. ستراها مرة واحدة فقط، وعلى العضو استبدالها عند أول تسجيل دخول. لا يُرسل أي بريد إلكتروني.',
  'merchant.staff.createAccountSubmit': 'إنشاء الحساب',
  'merchant.staff.accountCreatedTitle': 'تم إنشاء الحساب',
  'merchant.staff.reissue': 'كلمة مرور جديدة',
  'merchant.staff.reissueTitle': 'كلمة مرور مؤقتة جديدة',
  'merchant.staff.reissueIntro':
    'ستتوقف كلمة المرور الحالية لهذا العضو عن العمل وسيُسجَّل خروجه من كل مكان. اذكر السبب: يُحفظ في سجل التدقيق.',
  'merchant.staff.reissueReason': 'السبب (10 أحرف على الأقل)',
  'merchant.staff.reissueSubmit': 'إنشاء كلمة مرور جديدة',
  'merchant.staff.managedBadge': 'حساب أنشأه المتجر',
  'merchant.staff.table.colMember': 'العضو',
  'merchant.staff.table.colRole': 'الدور',
  'merchant.staff.roleOwner': 'المالك',
  'merchant.staff.roleStaff': 'موظف',
  'merchant.staff.statusSuspended': 'موقوف',
  'merchant.staff.fullAccess': 'وصول كامل',
  'merchant.staff.noPermissions': 'لا شيء',
  'merchant.staff.suspendAction': 'إيقاف',
  'merchant.staff.reactivateAction': 'إعادة التفعيل',
  'merchant.staff.removeAction': 'إزالة',
  'merchant.staff.removeConfirm': 'إزالة {{email}} من الفريق؟',
  'merchant.staff.suspendedToast': 'تم إيقاف العضو.',
  'merchant.staff.reactivatedToast': 'تمت إعادة تفعيل العضو.',
  'merchant.staff.removedToast': 'تمت إزالة العضو.',
  'merchant.staff.permissionsUpdatedToast': 'تم تحديث الصلاحيات.',
  'merchant.staff.table.colPermissions': 'الصلاحيات',
  'merchant.staff.form.emailLabel': 'عنوان البريد الإلكتروني',
  'merchant.staff.form.hint':
    'تعمل الدعوة فقط لعنوان لديه حساب ABOUEB مسبقاً. لن تتلقى تأكيداً مباشراً — تحقق من القائمة أدناه بعد الإرسال.',
  'merchant.staff.permission.catalog': 'الكتالوج (المنتجات، الفئات)',
  'merchant.staff.permission.analytics': 'التحليلات (قراءة)',

  // ---- merchant: settings ----
  'merchant.settings.subtitle': 'أدر متجرك وفريقك وتفضيلاتك من مكان واحد.',
  'merchant.settings.storeCardDescription':
    'الملف العام، أوقات العمل، السياسات، تحسين محركات البحث وصحة المتجر.',
  'merchant.settings.teamCardDescription': 'الدعوات والأدوار وصلاحيات أعضاء فريقك.',

  // ---- merchant: analytics ----
  'merchant.analytics.subtitle': 'مؤشرات حقيقية لمتجرك — محسوبة مباشرة من طلباتك ومنتجاتك ومخزونك.',
  'merchant.analytics.loadError': 'تعذر تحميل التحليلات',
  'merchant.analytics.revenue': 'رقم الأعمال',
  'merchant.analytics.revenueHint': 'عبر {{count}} طلب(ات) غير ملغاة',
  'merchant.analytics.avgOrderValue': 'متوسط السلة',
  'merchant.analytics.avgOrderValueEmptyHint': 'لا يوجد طلب غير ملغى حتى الآن.',
  'merchant.analytics.cancellationRate': 'معدل الإلغاء',
  'merchant.analytics.cancellationRateNoOrders': 'لا يوجد طلب مسجل.',
  'merchant.analytics.cancellationRateNoneCancelled': 'لا يوجد طلب ملغى حتى الآن.',
  'merchant.analytics.cancellationRateCount': '{{count}} طلب(ات) ملغاة',
  'merchant.analytics.stockAvailable': 'المخزون المتاح',
  'merchant.activity.title': 'النشاط الأخير',
  'merchant.activity.empty': 'لا يوجد نشاط حديث',
  'merchant.activity.loadError': 'تعذّر تحميل النشاط الأخير',
  'merchant.activity.reason': 'السبب: {{reason}}',
  'merchant.activity.actor.system': 'تلقائي',
  'merchant.activity.actor.customer': 'من العميل',
  'merchant.activity.actor.merchant': 'من المتجر',
  'merchant.activity.order.created': 'طلب جديد رقم {{number}}',
  'merchant.activity.order.changed': 'الطلب رقم {{number}}: {{to}}',
  'merchant.activity.stock.manual_adjustment': 'تعديل المخزون — {{item}}: {{delta}}',
  'merchant.activity.stock.confirmation': 'خروج من المخزون — {{item}}: {{delta}}',
  'merchant.activity.stock.release': 'تحرير من المخزون المحجوز — {{item}}: {{delta}}',
  'merchant.activity.stock.shipment': 'شحن — {{item}}: {{delta}}',
  'merchant.activity.stock.return': 'إعادة إلى المخزون — {{item}}: {{delta}}',
  'merchant.activity.stock.loss': 'فقدان مخزون — {{item}}: {{delta}}',
  'merchant.activity.stock.unknownItem': 'منتج',
  'merchant.nav.todo': 'للمعالجة',
  'merchant.todo.title': 'للمعالجة',
  'merchant.todo.subtitle': 'ما يتطلب انتباهك الآن، بحسب طلباتك ومخزونك وتوصيلك.',
  'merchant.todo.allClear': 'لا شيء للمعالجة حاليًا.',
  'merchant.todo.loading': 'جارٍ التحميل…',
  'merchant.todo.sectionError': 'تعذّر تحميل هذا القسم.',
  'merchant.todo.section.orders': 'الطلبات',
  'merchant.todo.section.stock': 'المخزون',
  'merchant.todo.section.delivery': 'التوصيل',
  'merchant.todo.section.jibex': 'حساب Jibex',
  'merchant.todo.severity.urgent': 'عاجل',
  'merchant.todo.severity.watch': 'للمتابعة',
  'merchant.todo.severity.info': 'معلومة',
  'merchant.todo.forbidden': 'هذا القسم غير متاح لدورك.',
  'merchant.todo.newShop.title': 'متجرك جاهز للانطلاق',
  'merchant.todo.newShop.description':
    'لا شيء للمعالجة حاليًا: أضف أول منتج وأكمل متجرك لتبدأ باستقبال الطلبات.',
  'merchant.todo.newShop.addProduct': 'إضافة منتج',
  'merchant.todo.newShop.completeShop': 'إكمال متجري',
  'merchant.todo.orders.toPrepare': '{{count}} طلب(ات) للتحضير',
  'merchant.todo.orders.toPrepareDescription': 'مؤكدة وتنتظر أن تبدأ تحضيرها.',
  'merchant.todo.orders.toPack': '{{count}} طلب(ات) للتغليف',
  'merchant.todo.orders.toPackDescription': 'قيد التحضير ولم تُعلَّم كمغلّفة بعد.',
  'merchant.todo.stock.out': '{{count}} منتج(ات) نافد(ة)',
  'merchant.todo.stock.outDescription': 'لم يعد بالإمكان طلبها.',
  'merchant.todo.stock.low': '{{count}} منتج(ات) بمخزون منخفض',
  'merchant.todo.stock.lowDescription': 'مخزونها دون حدّ التنبيه.',
  'merchant.todo.delivery.toShip': '{{count}} طلب(ات) مغلّفة بانتظار الشحن',
  'merchant.todo.delivery.toShipDescription': 'لم تغادر أي طرد بعد لهذه الطلبات.',
  'merchant.todo.delivery.failed': '{{count}} شحنة(ات) فاشلة',
  'merchant.todo.delivery.failedDescription': 'لم يتم الإرسال إلى شركة التوصيل: افتح صفحة التوصيل.',
  'merchant.todo.jibex.platformNotReady': 'توصيل Jibex غير متاح',
  'merchant.todo.jibex.platformNotReadyDescription': 'الخدمة غير مهيأة على المنصة بعد.',
  'merchant.todo.jibex.not_configured': 'حساب Jibex غير متصل',
  'merchant.todo.jibex.not_configuredDescription': 'بدون حساب لا يمكن إرسال أي طرد.',
  'merchant.todo.jibex.untested': 'حساب Jibex يحتاج إلى اختبار',
  'merchant.todo.jibex.untestedDescription': 'بيانات الدخول محفوظة لكنها لم تُفحص بعد.',
  'merchant.todo.jibex.invalid': 'رفضت Jibex بيانات الدخول',
  'merchant.todo.jibex.invalidDescription': 'رفضت Jibex بيانات الدخول: قم بتحديثها.',
  'merchant.todo.jibex.misconfigured': 'حساب Jibex غير مهيأ جيدًا',
  'merchant.todo.jibex.misconfiguredDescription':
    'الاتصال ناجح لكن الحساب غير قابل للاستخدام (الصلاحيات أو المتجر).',
  'merchant.todo.jibex.network_error': 'تعذّر الوصول إلى Jibex',
  'merchant.todo.jibex.network_errorDescription':
    'لم يتمكن آخر اختبار من الوصول إلى Jibex: أعد المحاولة.',
  'merchant.analytics.stockReserved': 'المخزون المحجوز',
  'merchant.analytics.topProductsTitle': 'المنتجات الأكثر طلباً',
  'merchant.analytics.topProductsEmptyTitle': 'لا يوجد منتج مطلوب حتى الآن',
  'merchant.analytics.topProductsEmptyDescription':
    'سيظهر الترتيب هنا فور استلام أول طلب غير ملغى.',
  'merchant.analytics.table.colQuantitySold': 'الكمية المباعة',

  // ---- merchant: promotions ----
  'merchant.promotions.subtitle': 'أنشئ وأدر قسائم الخصم القابلة للتطبيق على كامل السلة.',
  'merchant.promotions.ownerOnlyDescription': 'مالك المتجر وحده يمكنه إدارة القسائم.',
  'merchant.promotions.loadError': 'تعذر تحميل القسائم',
  'merchant.promotions.emptyTitle': 'لا توجد قسيمة حتى الآن',
  'merchant.promotions.emptyDescription': 'أنشئ قسيمتك الأولى لتقديم خصم على كامل السلة.',
  'merchant.promotions.editTitle': 'تعديل القسيمة',
  'merchant.promotions.updated': 'تم تحديث القسيمة.',
  'merchant.promotions.created': 'تم إنشاء القسيمة.',
  'merchant.promotions.deactivated': 'تم إلغاء تفعيل القسيمة.',
  'merchant.promotions.reactivated': 'تمت إعادة تفعيل القسيمة.',
  'merchant.promotions.table.colUsage': 'الاستخدام',
  'merchant.promotions.table.colValidityWindow': 'فترة الصلاحية',
  'merchant.promotions.statusInactive': 'معطّلة',
  'merchant.promotions.historyAction': 'السجل',
  'merchant.promotions.deactivateAction': 'إلغاء التفعيل',
  'merchant.promotions.noDateLimit': 'بدون حد زمني',
  'merchant.promotions.form.typeLabel': 'النوع',
  'merchant.promotions.form.typeFixed': 'مبلغ ثابت (دينار)',
  'merchant.promotions.form.typePercentage': 'نسبة مئوية (%)',
  'merchant.promotions.form.valuePercentLabel': 'القيمة (%)',
  'merchant.promotions.form.valueAmountLabel': 'القيمة (دينار)',
  'merchant.promotions.form.valueHint': 'من 0 إلى 100.',
  'merchant.promotions.form.minOrderLabel': 'الحد الأدنى لمبلغ الطلب',
  'merchant.promotions.form.minOrderHint': 'لا حد أدنى إذا تُرك فارغاً.',
  'merchant.promotions.form.usageLimitTotalLabel': 'الحد الأقصى الإجمالي للاستخدام',
  'merchant.promotions.form.usageLimitHint': 'غير محدود إذا تُرك فارغاً.',
  'merchant.promotions.form.usageLimitPerCustomerLabel': 'الحد الأقصى لكل عميل',
  'merchant.promotions.form.validFromLabel': 'صالحة اعتباراً من',
  'merchant.promotions.form.validUntilLabel': 'صالحة حتى',
  'merchant.promotions.form.activeCheckbox': 'قسيمة نشطة',
  'merchant.promotions.usageDrawer.title': 'السجل — {{code}}',
  'merchant.promotions.usageDrawer.loadError': 'تعذر تحميل السجل',
  'merchant.promotions.usageDrawer.neverUsed': 'لم تُستخدم هذه القسيمة قط',
  'merchant.promotions.usageDrawer.tableCaption': 'استخدامات القسيمة',
  'merchant.promotions.usageDrawer.colDiscount': 'الخصم',

  'errors.network': 'تعذر الوصول إلى الخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.',
  'errors.server': 'حدث خطأ من جانبنا. يرجى المحاولة مرة أخرى بعد قليل.',
  'errors.sessionExpired': 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.',
  'errors.forbidden': 'ليس لديك صلاحية الوصول إلى هذا المورد.',
  'errors.notFound': 'هذا المورد غير موجود أو لم يعد متاحاً.',
  'errors.validation': 'يحتوي النموذج على حقول غير صالحة. يرجى التحقق من معلوماتك.',
  'errors.conflict': 'يتعارض هذا الإجراء مع بيانات موجودة.',
  'errors.rateLimited': 'محاولات كثيرة جداً. يرجى المحاولة مرة أخرى بعد قليل.',
  'errors.passwordChangeRequired': 'يجب أن تختار أولًا كلمة مرور جديدة.',
  'errors.currentPasswordInvalid': 'كلمة المرور الحالية غير صحيحة.',
  'errors.passwordUnchanged': 'اختر كلمة مرور مختلفة عن الحالية.',
  'errors.passwordTooShort': 'كلمة المرور الجديدة قصيرة جدًا (12 حرفًا على الأقل).',
  'errors.staffAccountUnavailable': 'تعذّر إنشاء الحساب بهذا العنوان. جرّب عنوانًا آخر.',
  'errors.staffLimitReached': 'بلغ هذا المتجر الحد الأقصى لحجم فريقه.',
  'errors.shopNotActive': 'يجب تفعيل المتجر قبل إنشاء حسابات الفريق.',
  // Meta Pixel, social links, view shop, manual orders & sales (migration 0096).
  'merchant.store.form.social': 'الشبكات الاجتماعية',
  'merchant.store.form.socialHint':
    'الصق عناوين صفحاتك. تظهر في أعلى متجرك الشبكات التي تملأها فقط.',
  'merchant.store.form.socialWhatsappNote': 'يُضبط واتساب في قسم «الاتصال» ويظهر أيضاً مع شبكاتك.',
  'merchant.store.form.socialFacebook': 'صفحة فيسبوك',
  'merchant.store.form.socialInstagram': 'حساب إنستغرام',
  'merchant.store.form.socialTiktok': 'حساب تيك توك',
  'merchant.store.form.socialYoutube': 'قناة يوتيوب',
  'merchant.store.form.socialX': 'حساب X (تويتر)',
  'merchant.store.form.socialLinkedin': 'صفحة لينكدإن',
  'merchant.store.form.metaPixel': 'بكسل ميتا (فيسبوك وإنستغرام)',
  'merchant.store.form.metaPixelLabel': 'معرّف البكسل',
  'merchant.store.form.metaPixelHint':
    'رقم البكسل من مدير أحداث ميتا (مصادر البيانات). اتركه فارغاً لعدم التتبع.',
  'merchant.store.form.metaPixelPrivacy':
    'لا يُحمَّل البكسل إلا بعد موافقة الزائر (شريط ملفات تعريف الارتباط). يتتبع المشاهدات والإضافة إلى السلة وبدء الطلب والطلبات المنجزة.',
  'merchant.viewShop.label': 'عرض المتجر',
  'merchant.viewShop.short': 'المتجر',
  'merchant.viewShop.notPublic': 'متجرك غير ظاهر على الإنترنت بعد.',
  'merchant.viewShop.noStore': 'أنشئ متجرك أولاً: الاسم والعنوان العام.',
  'merchant.nav.sales': 'المبيعات',
  'merchant.orders.add': 'إضافة طلب',
  'merchant.orders.sourceManual': 'إدخال يدوي',
  'merchant.orders.sourceSale': 'بيع يدوي',
  'merchant.sales.title': 'المبيعات',
  'merchant.sales.subtitle':
    'المبيعات المسجلة يدوياً (متجر فعلي، سوق، هاتف…). تُحتسب في رقم المعاملات مثل الطلبات عبر الإنترنت، مرة واحدة فقط.',
  'merchant.sales.add': 'إضافة عملية بيع',
  'merchant.sales.count': 'المبيعات المسجلة',
  'merchant.sales.paidAmount': 'المحصّل (هذه الصفحة)',
  'merchant.sales.toCollect': 'للتحصيل (هذه الصفحة)',
  'merchant.sales.loadError': 'تعذّر تحميل المبيعات.',
  'merchant.sales.empty': 'لا توجد مبيعات مسجلة',
  'merchant.sales.emptyHint':
    'سجّل هنا عملية بيع تمت خارج المتجر الإلكتروني: يتحدّث المخزون ورقم المعاملات.',
  'merchant.sales.colPayment': 'الدفع',
  'merchant.sales.colDate': 'تاريخ البيع',
  'merchant.sales.previous': 'السابق',
  'merchant.sales.next': 'التالي',
  'merchant.manual.newOrderTitle': 'إضافة طلب',
  'merchant.manual.newOrderSubtitle':
    'طلب وصلك عبر الهاتف أو رسالة أو مباشرة. يتبع المسار المعتاد: التحضير، الطرد، التوصيل.',
  'merchant.manual.newSaleTitle': 'إضافة عملية بيع',
  'merchant.manual.newSaleSubtitle': 'عملية بيع سُلّمت للزبون. يتحدّث المخزون وتُنشأ فاتورة.',
  'merchant.manual.backToOrders': '→ الطلبات',
  'merchant.manual.backToSales': '→ المبيعات',
  'merchant.manual.customer': 'الزبون',
  'merchant.manual.existingCustomer': 'زبون حالي',
  'merchant.manual.newCustomer': '— زبون جديد —',
  'merchant.manual.customerName': 'اسم الزبون',
  'merchant.manual.customerPhone': 'الهاتف',
  'merchant.manual.customerPhoneHint': 'رقم تونسي، مع أو بدون ‎+216.',
  'merchant.manual.customerEmail': 'البريد الإلكتروني',
  'merchant.manual.products': 'المنتجات',
  'merchant.manual.loadingProducts': 'جارٍ تحميل الكتالوج…',
  'merchant.manual.noActiveProducts': 'لا يوجد منتج نشط: فعّل منتجاً واحداً على الأقل لتسجيل طلب.',
  'merchant.manual.product': 'المنتج',
  'merchant.manual.chooseProduct': 'اختر منتجاً',
  'merchant.manual.variant': 'النوع',
  'merchant.manual.chooseVariant': 'اختر',
  'merchant.manual.quantity': 'الكمية',
  'merchant.manual.removeLine': 'إزالة',
  'merchant.manual.addLine': '+ إضافة منتج',
  'merchant.manual.stockAvailable': '{{count}} في المخزون',
  'merchant.manual.stockUntracked': 'المخزون غير متابَع',
  'merchant.manual.stockShort': 'المخزون غير كافٍ',
  'merchant.manual.coupon': 'رمز التخفيض',
  'merchant.manual.couponHint':
    'يُطبَّق حسب شروطه (التواريخ، الحد الأدنى، الحدود) ويُتحقق منه عند الحفظ.',
  'merchant.manual.delivery': 'التوصيل',
  'merchant.manual.handover': 'التسليم',
  'merchant.manual.pickup': 'تسليم مباشر في المتجر (بدون توصيل)',
  'merchant.manual.governorate': 'الولاية',
  'merchant.manual.shopGovernorate': 'ولاية المتجر',
  'merchant.manual.chooseGovernorate': 'اختر…',
  'merchant.manual.chooseGovernorateFirst': 'اختر الولاية أولاً',
  'merchant.manual.errors.delegationMismatch': 'هذه المعتمدية لا تنتمي إلى الولاية المختارة.',
  'merchant.manual.delegation': 'المعتمدية',
  'merchant.manual.address': 'العنوان',
  'merchant.manual.additionalInfo': 'تفاصيل العنوان',
  'merchant.manual.applyDeliveryFee': 'تطبيق رسوم التوصيل الخاصة بالمتجر ({{fee}})',
  'merchant.manual.paymentAndStatus': 'الدفع والحالة',
  'merchant.manual.status': 'حالة الطلب',
  'merchant.manual.status.toProcess': 'للمعالجة',
  'merchant.manual.status.toProcessHint': 'يظهر في «المهام» مثل طلب عبر الإنترنت.',
  'merchant.manual.status.processing': 'قيد التحضير',
  'merchant.manual.status.processingHint': 'بدأت تحضيره: الخطوة التالية هي الطرد.',
  'merchant.manual.paymentMethod': 'طريقة الدفع',
  'merchant.manual.paymentStatus': 'حالة الدفع',
  'merchant.manual.paymentPaid': 'مدفوعة',
  'merchant.manual.paymentPending': 'للتحصيل',
  'merchant.manual.orderPaymentPending': 'الدفع للتحصيل: لا يُسجَّل الطلب اليدوي كمدفوع أبداً.',
  'merchant.manual.paymentReference': 'مرجع الدفع',
  'merchant.manual.paymentReferenceHint':
    'رقم التحويل أو الصك أو وصل البطاقة. إلزامي لعملية بيع مدفوعة بغير النقد.',
  'merchant.manual.saleDate': 'تاريخ البيع',
  'merchant.manual.saleDateHint': 'لا يتجاوز 30 يوماً إلى الوراء، ولا يكون في المستقبل.',
  'merchant.manual.internalNote': 'ملاحظة داخلية',
  'merchant.manual.internalNoteHint': 'تراها فرقتك فقط، ولا يراها الزبون أبداً.',
  'merchant.manual.subtotal': 'المجموع الفرعي',
  'merchant.manual.deliveryFee': 'التوصيل',
  'merchant.manual.discount': 'التخفيض',
  'merchant.manual.discountOnSave': 'تُحتسب عند الحفظ',
  'merchant.manual.estimatedTotal': 'المجموع التقديري',
  'merchant.manual.serverRecomputes':
    'يعيد الخادم احتساب الأسعار والتخفيض والرسوم والمجموع من الكتالوج، ويتحقق من المخزون في الوقت نفسه.',
  'merchant.manual.saveOrder': 'حفظ الطلب',
  'merchant.manual.saveSale': 'حفظ عملية البيع',
  'merchant.manual.saving': 'جارٍ الحفظ…',
  'merchant.manual.orderCreated': 'تم إنشاء الطلب رقم {{number}} — {{total}}.',
  'merchant.manual.saleCreated': 'تم تسجيل البيع رقم {{number}} — {{total}}.',
  'merchant.manual.payment.cod': 'الدفع عند الاستلام',
  'merchant.manual.payment.cash': 'نقداً',
  'merchant.manual.payment.bankTransfer': 'تحويل بنكي',
  'merchant.manual.payment.card': 'بطاقة بنكية',
  'merchant.manual.payment.cheque': 'صك',
  'merchant.manual.payment.other': 'أخرى',
  'merchant.manual.errors.fixForm': 'تحقّق من الحقول المشار إليها.',
  'merchant.manual.errors.nameRequired': 'اسم الزبون مطلوب.',
  'merchant.manual.errors.phoneInvalid': 'رقم هاتف تونسي غير صالح.',
  'merchant.manual.errors.emailInvalid': 'بريد إلكتروني غير صالح.',
  'merchant.manual.errors.productRequired': 'اختر منتجاً.',
  'merchant.manual.errors.variantRequired': 'اختر نوعاً.',
  'merchant.manual.errors.quantityInvalid': 'الكمية بين 1 و999.',
  'merchant.manual.errors.duplicateLine': 'هذا المنتج موجود في القائمة: غيّر كميته.',
  'merchant.manual.errors.governorateRequired': 'اختر ولاية.',
  'merchant.manual.errors.delegationRequired': 'المعتمدية مطلوبة.',
  'merchant.manual.errors.addressRequired': 'العنوان مطلوب.',
  'merchant.manual.errors.referenceRequired': 'أدخل مرجع الدفع.',
  'merchant.manual.errors.saleDate': 'تاريخ البيع: 30 يوماً كحد أقصى إلى الوراء، وليس في المستقبل.',
  'errors.saleDateOutOfRange': 'يجب أن يكون تاريخ البيع خلال آخر 30 يوماً وليس في المستقبل.',
  'errors.storeNotConfigured': 'اضبط متجرك أولاً (قائمة المتجر).',
  'errors.requestInProgress': 'هذا التسجيل قيد الحفظ. انتظر بضع ثوانٍ.',
  'storefront.social.label': 'تابعونا',
  'storefront.social.open': '{{network}} (علامة تبويب جديدة)',
  'storefront.consent.title': 'ملفات تعريف الارتباط الإعلانية',
  'storefront.consent.text':
    'يستخدم هذا المتجر بكسل ميتا (فيسبوك، إنستغرام) لقياس إعلاناته، فقط إذا وافقت. تبقى ملفات الارتباط الضرورية للسلة مفعّلة.',
  'storefront.consent.accept': 'قبول',
  'storefront.consent.decline': 'رفض',
  'storefront.consent.manage': 'ملفات الارتباط',
  // Order attribution — « Ventes par réseau » (migration 0097).
  'merchant.attribution.title': 'المبيعات حسب القناة',
  'merchant.attribution.total': 'المجموع',
  'merchant.attribution.subtitle':
    'من أين تأتي طلباتك: تطبيقات التواصل الاجتماعي، الإعلانات، البحث أو الطلبات المُدخلة يدوياً.',
  'merchant.attribution.empty': 'لا توجد طلبات لتحليلها بعد.',
  'merchant.attribution.orders': '{{count}} طلب',
  'merchant.attribution.average': 'متوسط السلة: {{amount}}',
  'merchant.attribution.howItWorks':
    'تُحتسب قناة آخر زيارة جاءت من شبكة أو إعلان (الزيارة المباشرة لا تلغيها): رابط يحمل معاملات utm (روابط «مشاركة متجري» تحملها)، إعلان ميتا / تيك توك / غوغل، أو المتصفح المدمج في التطبيق (إنستغرام، فيسبوك، تيك توك…). «غير مسجّل»: طلبات أقدم أو مصدر لا يمكن معرفته. لا تُجمع أي بيانات شخصية.',
  'merchant.attribution.via': 'عبر {{source}}',
  'merchant.attribution.email': 'البريد الإلكتروني',
  'merchant.attribution.referral': 'موقع آخر',
  'merchant.attribution.other': 'أخرى',
  'merchant.attribution.direct': 'دخول مباشر',
  'merchant.attribution.phone': 'الهاتف',
  'merchant.attribution.inStore': 'في المتجر',
  'merchant.attribution.manual': 'إدخال يدوي (بدون قناة)',
  'merchant.attribution.unknown': 'غير مسجّل',
  'merchant.attribution.channel': 'وردت عبر',
  'merchant.attribution.channelHint':
    'القناة التي تواصل بها الزبون معك: تُحتسب في «المبيعات حسب القناة».',
  'merchant.attribution.channelNone': 'غير محدد',
  'merchant.attribution.bioLinks': 'روابط لحساباتك على الشبكات',
  'merchant.attribution.bioLinksHint':
    'كل رابط موسوم بشبكته: الطلبات التي تتم بعد النقر تظهر «عبر إنستغرام»، «عبر تيك توك»…',
  'merchant.attribution.copyFor': 'نسخ لـ {{source}}',
  // Real-time dashboard (migration 0098).
  'merchant.live.on': 'مباشر',
  'merchant.live.connecting': 'جارٍ الاتصال…',
  'merchant.live.off': 'غير متصل',
  'merchant.newOrder.title': 'طلب جديد #{{number}}',
  'merchant.newOrder.view': 'عرض الطلبات',
  'merchant.newOrder.dismiss': 'إغلاق',
  'merchant.newOrder.enableDesktop': 'استلام الطلبات الجديدة كإشعارات على هذا الجهاز',
  'merchant.cancelOrder.action': 'إلغاء',
  'merchant.cancelOrder.title': 'إلغاء الطلب #{{number}}',
  'merchant.cancelOrder.intro':
    'يصبح الطلب « ملغى » وتعود المنتجات المحجوزة إلى البيع. لا يمكن التراجع عن هذا الإجراء.',
  'merchant.cancelOrder.reasonLegend': 'سبب الإلغاء',
  'merchant.cancelOrder.reason.unreachable': 'تعذّر الاتصال بالحريف',
  'merchant.cancelOrder.reason.outOfStock': 'نفاد المخزون',
  'merchant.cancelOrder.reason.duplicate': 'طلب مكرر',
  'merchant.cancelOrder.reason.customerChanged': 'غيّر الحريف رأيه',
  'merchant.cancelOrder.reason.other': 'سبب آخر',
  'merchant.cancelOrder.detail': 'توضيح',
  'merchant.cancelOrder.detailRequired': 'وضّح السبب.',
  'merchant.cancelOrder.reasonRequired': 'اختر سبب الإلغاء.',
  'merchant.cancelOrder.parcelAtCarrier':
    'الطرد موجود لدى Jibex: ألغِ الطرد أولاً من قسم التوصيل ثم الطلب.',
  'merchant.cancelOrder.keep': 'الإبقاء على الطلب',
  'merchant.cancelOrder.confirm': 'إلغاء الطلب',
  'merchant.cancelOrder.done': 'تم إلغاء الطلب #{{number}}.',
  'merchant.push.title': 'الطلبات الجديدة على هذا الهاتف',
  'merchant.push.offHint': 'استلم كل طلب جديد في شريط الإشعارات، حتى عندما يكون الموقع مغلقًا.',
  'merchant.push.onHint': 'مفعّل على هذا الجهاز: يصل كل طلب جديد إلى شريط الإشعارات.',
  'merchant.push.turnOn': 'تفعيل الإشعارات',
  'merchant.push.turnOff': 'إيقاف على هذا الجهاز',
  'merchant.push.enabled': 'تم التفعيل. ستُعلَم بكل طلب جديد.',
  'merchant.push.disabled': 'تم الإيقاف على هذا الجهاز.',
  'merchant.push.blocked': 'الإشعارات محظورة: اسمح بها لهذا الموقع من إعدادات المتصفح.',
  'merchant.push.failed': 'لم ينجح التفعيل. أعد المحاولة بعد لحظة.',
  'merchant.push.iosIntro': 'على iPhone، تطلب Apple أولاً إضافة ABOUEB إلى الشاشة الرئيسية:',
  'merchant.push.iosStep1': 'في Safari، اضغط على زر المشاركة.',
  'merchant.push.iosStep2': 'اختر « إضافة إلى الشاشة الرئيسية » ثم « إضافة ».',
  'merchant.push.iosStep3': 'افتح ABOUEB من الأيقونة الجديدة وفعّل الإشعارات من هنا.',
  'merchant.live.hint':
    'تتحدّث الطلبات والطرود تلقائياً دون «تحديث». عند عدم الاتصال: تحديث كل 30 ثانية.',
};
